import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { MatSelectChange } from '@angular/material/select';
import { ActivatedRoute, Router } from '@angular/router';
import {
  BehaviorSubject,
  catchError,
  combineLatest,
  debounceTime,
  distinctUntilChanged,
  finalize,
  forkJoin,
  map,
  Observable,
  of,
  startWith,
  Subscription,
  switchMap,
} from 'rxjs';
import {
  HrAlertService,
  HrHttpService,
  MrfDocumentView,
  MRF_DOCUMENT_TYPE,
  SaveMrfFormPayload,
} from '../hr-http.service';
import { HmfPosTab } from '../mrf-types';
import {
  EMPTY_MRF_HRMS_PERMISSIONS,
  MrfHrmsPermissions,
  formatMrfDateForDisplay,
  formatMrfDateForInput,
  formatMrfDateForPayload,
  getMrfAadhaarValidationError,
  getMrfWorkloadValidationError,
  getMrfYearsOfExperienceValidationError,
  normalizeMrfAadhaarDigits,
  normalizeMrfWorkloadValue,
  normalizeMrfYearsOfExperienceValue,
  resolveMrfRehiringAadharNo,
  resolveMrfRehiringAadharPreferringSaved,
  syncMrfAadhaarControlFromInput,
  syncMrfWorkloadControlFromInput,
  syncMrfYearsOfExperienceControlFromInput,
  resolveMrfYearsOfExperience,
  formatMrfRequestStatusLabel,
  formatMrfOnboardingStatusLabel,
  getMrfRequestStatusTone,
  getMrfOnboardingStatusTone,
  resolveMrfRequestStatusRaw,
  resolveMrfOnboardingStatusRaw,
  getMrfUploadFileSizeValidationMessage,
} from '../mrf-view-options';
import { NgxSpinnerService } from 'ngx-spinner';

@Component({
  selector: 'app-manpower-requisition-form',
  templateUrl: './manpower-requisition-form.component.html',
  styleUrls: ['./manpower-requisition-form.component.scss'],
})
export class ManpowerRequisitionFormComponent implements OnInit, OnDestroy {
  private static readonly LEFT_EMP_MIN_SEARCH_LENGTH = 5;

  posTab: HmfPosTab = 'Replacement';
  rehiring = false;
  /** When true, rehiring fields are read-only and bound only via LeftEmpSearch. */
  rehiringDataLocked = false;
  showRehiringSection = true;
  availableHeadcount = '00';
  budgetedPositions = '';
  headcountError = '';
  private headcountBudgetTotal = 0;
  selectedCampusId: number | string | null = null;
  selectedTeachingTypeId: number | string | null = null;
  showSubjectSelection = false;
  isSaving = false;
  readonly maxRehiringAadhaarFiles = 1;
  rehiringAadhaarPendingFiles: File[] = [];
  rehiringAadhaarUploadError = '';
  rehiringAadhaarDocuments: MrfDocumentView[] = [];

  entity = new FormControl('');
  campus = new FormControl('');
  employeeType = new FormControl('');
  teachingType = new FormControl('');
  department = new FormControl('');
  designation = new FormControl('');
  subject = new FormControl('');
  posDesignation = new FormControl('');
  posDesignation2 = new FormControl('');
  skills = new FormControl('');
  qualification = new FormControl('');
  additionalQualification = new FormControl('');
  reportingManager = new FormControl('');
  workload = new FormControl('');
  hiringReason = new FormControl('');
  reasonDescription = new FormControl('');
  readonly reasonDescriptionOptions = ['Transferred', 'Exit', 'Promoted'] as const;
  prevChaitanyaId = new FormControl('');
  rehiringDob = new FormControl('');
  aadharNo = new FormControl('');
  salary = new FormControl('');
  yearsOfExp = new FormControl('');
  employeeId = new FormControl<string | Record<string, unknown>>('');
  replacementName = new FormControl('');
  replacementDesignation = new FormControl('');
  lastWorkingDate = new FormControl('');

  campus2 = new FormControl('');
  employeeType2 = new FormControl('');
  teachingType2 = new FormControl('');
  department2 = new FormControl('');
  designation2 = new FormControl('');
  subject2 = new FormControl('');
  costCenter = new FormControl('');
  costCenter2 = new FormControl('');
  qualification2 = new FormControl('');
  additionalQualification2 = new FormControl('');
  reportingManager2 = new FormControl('');
  noOfHeadcount = new FormControl('1');

  entityOptions: any = [];
  campusOptions = of<any[]>([]);
  employeeTypeOptions = of<any[]>([]);
  teachingTypeOptions = of<any[]>([]);
  departmentOptions = of<any[]>([]);
  designationOptions = of<any[]>([]);
  subjectOptions = of<any[]>([]);
  posDesignationOptions = of<any[]>([]);
  leftEmpOptionsList: Record<string, unknown>[] = [];
  prevLeftEmpOptionsList: Record<string, unknown>[] = [];
  private readonly leftEmpOptionsSubject = new BehaviorSubject<Record<string, unknown>[]>([]);
  private readonly prevLeftEmpOptionsSubject = new BehaviorSubject<Record<string, unknown>[]>([]);
  leftEmpOptions = this.leftEmpOptionsSubject.asObservable();
  prevLeftEmpOptions = this.prevLeftEmpOptionsSubject.asObservable();
  qualificationOptions = of<any[]>([]);
  additionalQualificationOptions = of<any[]>([]);
  reportingManagerOptions = of<any[]>([]);

  employeeTypeOptionsList: any[] = [];
  departmentOptionsList: any[] = [];
  subjectOptionsList: any[] = [];
  campusOptionsList: any[] = [];
  teachingTypeOptionsList: any[] = [];
  costCenterOptionsList: any[] = [];
  costCenterOptions = of<any[]>([]);
  reportingManagerOptionsList: any[] = [];

  private readonly qualificationOptionsList$ = new BehaviorSubject<any[]>([]);
  private readonly designationOptionsList$ = new BehaviorSubject<any[]>([]);
  private readonly teachingTypeList$ = new BehaviorSubject<any[]>([]);
  private readonly departmentList$ = new BehaviorSubject<any[]>([]);
  private readonly reportingManagerList$ = new BehaviorSubject<any[]>([]);
  private readonly subs = new Subscription();
  private lastSelectedCampus = '';
  private lastSelectedDepartment = '';
  private lastSelectedDesignation = '';
  replacementEmployeeDetails: any| null = null;
  prevRehiringEmployeeDetails: Record<string, unknown> | null = null;
  editingMrfId: string | null = null;
  private pendingEditData: Record<string, unknown> | null = null;
  userRoleDetails: Record<string, unknown> | null = null;
  mrfPermissions: MrfHrmsPermissions = { ...EMPTY_MRF_HRMS_PERMISSIONS };

  get canEditRequestForm(): boolean {
    return this.mrfPermissions.requestForm;
  }

  get requestStatusLabel(): string {
    return formatMrfRequestStatusLabel(resolveMrfRequestStatusRaw(this.pendingEditData));
  }

  get requestStatusTone(): string {
    return getMrfRequestStatusTone(resolveMrfRequestStatusRaw(this.pendingEditData));
  }

  get onboardingStatusLabel(): string {
    return formatMrfOnboardingStatusLabel(
      resolveMrfOnboardingStatusRaw(this.pendingEditData)
    );
  }

  get onboardingStatusTone(): string {
    return getMrfOnboardingStatusTone(
      resolveMrfOnboardingStatusRaw(this.pendingEditData)
    );
  }

  constructor(
    private hrHttpService: HrHttpService,
    private spinner: NgxSpinnerService,
    private route: ActivatedRoute,
    private router: Router,
    private hrAlert: HrAlertService
  ) {}

  ngOnInit(): void {
    this.initFilteredOptions();

    this.subs.add(
      this.campus2.valueChanges.subscribe((campusName) => {
        this.handleCampusChange(campusName);
      })
    );

    this.subs.add(
      this.department2.valueChanges.subscribe((deptName) => {
        const normalized = String(deptName ?? '').trim();

        if (!normalized) {
          this.lastSelectedDepartment = '';
          this.clearDesignationFields();
          this.clearSubjectFields();
          return;
        }

        const departmentChanged =
          this.lastSelectedDepartment !== '' && this.lastSelectedDepartment !== normalized;
        if (departmentChanged) {
          this.clearDesignationFields();
          this.clearSubjectFields();
        }
        this.lastSelectedDepartment = normalized;

        const dept = this.departmentOptionsList.find(
          (d) => (d?.deptName ?? d?.departmentName ?? d?.name) === normalized
        );
        if (dept) {
          this.setDesignationListForDepartment(dept);
        }
        this.refreshHeadCountInfo();
      })
    );

    this.subs.add(
      this.designation2.valueChanges.subscribe((desigName) => {
        const normalized = String(desigName ?? '').trim();

        if (!normalized) {
          this.lastSelectedDesignation = '';
          this.clearSubjectFields();
          this.updateSubjectVisibilityFromDesignation(null);
          this.refreshHeadCountInfo();
          return;
        }

        const designationChanged =
          this.lastSelectedDesignation !== '' && this.lastSelectedDesignation !== normalized;
        if (designationChanged) {
          this.clearSubjectFields();
        }
        this.lastSelectedDesignation = normalized;
        this.updateSubjectVisibilityFromDesignation(normalized);
        if (this.isPosTab('New Position')) {
          this.setSelectPair(this.posDesignation2, this.posDesignation, normalized);
        }
        this.refreshHeadCountInfo();
      })
    );

    this.subs.add(
      this.posDesignation2.valueChanges.subscribe((desigName) => {
        if (!this.isPosTab('New Position')) {
          return;
        }
        const normalized = String(desigName ?? '').trim();
        if (!normalized) {
          return;
        }
        if (String(this.designation2.value ?? '').trim() !== normalized) {
          this.setSelectPair(this.designation2, this.designation, normalized);
        }
        if (this.lastSelectedDesignation !== normalized) {
          this.lastSelectedDesignation = normalized;
          this.updateSubjectVisibilityFromDesignation(normalized);
          this.refreshHeadCountInfo();
        }
      })
    );

    this.subs.add(
      this.subject2.valueChanges.subscribe(() => {
        if (this.isNonTeachRoleType()) {
          return;
        }
        this.refreshHeadCountInfo();
      })
    );

    this.subs.add(
      this.noOfHeadcount.valueChanges.subscribe(() => this.updateAvailableHeadcountDisplay())
    );

    this.subs.add(
      this.reasonDescription.valueChanges.subscribe((value) => {
        if (String(value ?? '').trim().toLowerCase() !== 'exit') {
          this.lastWorkingDate.setValue('');
        }
      })
    );

    this.subs.add(
      this.route.queryParamMap.subscribe((params) => {
        const mrfId = params.get('mrfId');
        if (mrfId) {
          this.editingMrfId = mrfId;
          this.loadFormWithExistingData(mrfId);
        } else {
          this.editingMrfId = null;
          this.loadNewFormMasterData();
        }
      })
    );
  }

  private loadNewFormMasterData(): void {
    this.showRehiringSection = true;
    this.rehiring = false;
    this.clearRehiringAadhaarUploads();
    this.spinner.show();
    forkJoin({
      campuses: this.safeApiCall(this.hrHttpService.getEmpCampuses()),
      basicInfo: this.safeApiCall(this.hrHttpService.getMrfBasicInfo()),
      teachTypes: this.safeApiCall(this.hrHttpService.getMrfTeachTypes()),
      costCenters: this.safeApiCall(this.hrHttpService.getMrfCostCenterDetails()),
      qualifications: this.safeApiCall(this.hrHttpService.getMrfQualifications()),
      roles: this.safeApiCall(this.hrHttpService.getUserRoles()),
    })
      .pipe(finalize(() => this.spinner.hide()))
      .subscribe({
        next: (results) => {
          this.applyMasterDataFromResponses({ ...results, reportingManagers: [] });
          this.setReportingManagerOptionsList([]);
          this.campus.setValue('');
          this.campus2.setValue('', { emitEvent: false });
          this.selectedCampusId = null;
          this.lastSelectedCampus = '';
          this.employeeType.setValue('');
          this.employeeType2.setValue('', { emitEvent: false });
          this.teachingType.setValue('');
          this.teachingType2.setValue('', { emitEvent: false });
          this.selectedTeachingTypeId = null;
          this.clearDepartmentAndDesignation();
          this.subject.setValue('');
          this.subject2.setValue('', { emitEvent: false });
        },
        error: (err) => console.error(err),
      });
  }

  ngOnDestroy(): void {
    this.revokeRehiringAadhaarDocumentUrls();
    this.subs.unsubscribe();
  }

  /** Create filter observables once — avoids mat-select/autocomplete dispose errors on reassignment. */
  private initFilteredOptions(): void {
    this.campusOptions = this.campus.valueChanges.pipe(
      startWith(''),
      map((value: any) =>
        this.Filter(this.filterSearchValue(value, 'campusName'), this.campusOptionsList, 'campusName')
      )
    );

    this.costCenterOptions = this.costCenter.valueChanges.pipe(
      startWith(''),
      map((value: any) =>
        this.Filter(
          this.filterSearchValue(value, 'costCenterName'),
          this.costCenterOptionsList,
          'costCenterName'
        )
      )
    );

    this.employeeTypeOptions = this.employeeType.valueChanges.pipe(
      startWith(''),
      map((value: any) =>
        this.Filter(this.filterSearchValue(value, 'employeeType'), this.employeeTypeOptionsList, 'employeeType')
      )
    );

    this.teachingTypeOptions = combineLatest([
      this.teachingType.valueChanges.pipe(startWith('')),
      this.teachingTypeList$,
    ]).pipe(
      map(([value, list]) =>
        this.Filter(this.filterSearchValue(value, 'roleType'), list, 'roleType')
      )
    );

    this.departmentOptions = combineLatest([
      this.department.valueChanges.pipe(startWith('')),
      this.departmentList$,
    ]).pipe(
      map(([value, list]) =>
        this.Filter(this.filterSearchValue(value, 'deptName'), list, 'deptName')
      )
    );

    this.designationOptions = this.designation.valueChanges.pipe(
      startWith(''),
      switchMap((value: any) =>
        this.designationOptionsList$.pipe(
          map((list) =>
            this.Filter(
              this.filterSearchValue(value, 'desigName'),
              list,
              'desigName',
              ['designation', 'designationName']
            )
          )
        )
      )
    );

    this.subjectOptions = this.subject.valueChanges.pipe(
      startWith(''),
      map((value: any) =>
        this.Filter(
          this.filterSearchValue(value, 'subName'),
          this.subjectOptionsList,
          'subName',
          ['subjectName', 'subject']
        )
      )
    );

    this.posDesignationOptions = this.posDesignation.valueChanges.pipe(
      startWith(''),
      switchMap((value: any) =>
        this.designationOptionsList$.pipe(
          map((list) =>
            this.Filter(
              this.filterSearchValue(value, 'desigName'),
              list,
              'desigName',
              ['designation', 'designationName']
            )
          )
        )
      )
    );

    this.qualificationOptions = combineLatest([
      this.qualification.valueChanges.pipe(startWith('')),
      this.qualificationOptionsList$,
    ]).pipe(
      map(([value, list]) =>
        this.Filter(
          this.filterSearchValue(value, 'qualificationName'),
          list,
          'qualificationName',
          ['qualification', 'qualName', 'educationQualification', 'name']
        )
      )
    );

    this.additionalQualificationOptions = combineLatest([
      this.additionalQualification.valueChanges.pipe(startWith('')),
      this.qualificationOptionsList$,
    ]).pipe(
      map(([value, list]) =>
        this.Filter(
          this.filterSearchValue(value, 'qualificationName'),
          list,
          'qualificationName',
          ['qualification', 'qualName', 'educationQualification', 'name']
        )
      )
    );

    this.reportingManagerOptions = combineLatest([
      this.reportingManager.valueChanges.pipe(startWith('')),
      this.reportingManagerList$,
    ]).pipe(
      map(([value, list]) =>
        this.Filter(
          this.filterSearchValue(value, 'reportingManagerName'),
          list,
          'reportingManagerName',
          [
            'managerName',
            'empName',
            'employeeName',
            'userName',
            'name',
            'payrollId',
            'payRollId',
            'desigName',
            'designation',
            'desig',
            'managerDesignation',
          ]
        )
      )
    );

    this.subs.add(
      this.buildLeftEmpSearchStream(this.employeeId, 'replacement').subscribe()
    );
    this.subs.add(
      this.buildLeftEmpSearchStream(this.prevChaitanyaId, 'rehiring').subscribe()
    );
  }

  refreshLeftEmpAutocomplete(target: 'replacement' | 'rehiring'): void {
    if (target === 'rehiring' && this.rehiringDataLocked) {
      return;
    }
    const control = target === 'rehiring' ? this.prevChaitanyaId : this.employeeId;
    const search = this.getLeftEmpSearchText(control.value);
    if (!search) {
      this.publishLeftEmpOptions(target);
      return;
    }
    if (search.length < ManpowerRequisitionFormComponent.LEFT_EMP_MIN_SEARCH_LENGTH) {
      const existing =
        target === 'rehiring' ? this.prevLeftEmpOptionsList : this.leftEmpOptionsList;
      this.setLeftEmpOptionsList(this.filterLeftEmpOptionsBySearch(search, existing), target);
      return;
    }
    this.subs.add(this.fetchLeftEmpOptions(search, target).subscribe());
  }

  private buildLeftEmpSearchStream(
    control: FormControl,
    target: 'replacement' | 'rehiring'
  ): Observable<Record<string, unknown>[]> {
    return control.valueChanges.pipe(
      startWith(''),
      debounceTime(300),
      map((value: unknown) => this.getLeftEmpSearchText(value)),
      distinctUntilChanged(),
      switchMap((search: string) => {
        const existing =
          target === 'rehiring' ? this.prevLeftEmpOptionsList : this.leftEmpOptionsList;
        if (search.length < ManpowerRequisitionFormComponent.LEFT_EMP_MIN_SEARCH_LENGTH) {
          const options = search ? this.filterLeftEmpOptionsBySearch(search, existing) : [];
          return of(this.setLeftEmpOptionsList(options, target));
        }
        return this.fetchLeftEmpOptions(search, target);
      })
    );
  }

  private fetchLeftEmpOptions(
    search: string,
    target: 'replacement' | 'rehiring'
  ): Observable<Record<string, unknown>[]> {
    const existing =
      target === 'rehiring' ? this.prevLeftEmpOptionsList : this.leftEmpOptionsList;
    return this.searchLeftEmpByTarget(search, target).pipe(
      map((res) => this.hrHttpService.normalizeLeftEmpSearchResults(res)),
      map((list) => this.mergeLeftEmpOptions(existing, list)),
      map((list) => this.setLeftEmpOptionsList(list, target)),
      catchError((err) => {
        const apiLabel =
          target === 'replacement' ? 'currentAndLeftEmpSearch' : 'LeftEmpSearch';
        console.error(`${apiLabel} failed`, err);
        const fallback = this.filterLeftEmpOptionsBySearch(search, existing);
        return of(this.setLeftEmpOptionsList(fallback, target));
      })
    );
  }

  private searchLeftEmpByTarget(
    search: string,
    target: 'replacement' | 'rehiring'
  ): Observable<unknown> {
    if (target === 'replacement') {
      return this.hrHttpService.searchCurrentAndLeftEmployees(search, this.selectedCampusId);
    }
    const aadharNo = normalizeMrfAadhaarDigits(search);
    const searchByAadharOnly = aadharNo.length === 12 && /^\d+$/.test(aadharNo);
    return this.hrHttpService.searchLeftEmployees(searchByAadharOnly ? '' : search, {
      aadharNo: searchByAadharOnly ? aadharNo : undefined,
    });
  }

  private filterLeftEmpOptionsBySearch(
    search: string,
    list: Record<string, unknown>[]
  ): Record<string, unknown>[] {
    return this.Filter(search, list, 'payrollId', [
      'payRollId',
      'employeeId',
      'empId',
      'empName',
      'userName',
      'employeeName',
      'name',
      'mobileNo',
      'mobileNumber',
      'mobile',
      'phoneNo',
      'contactNo',
      'aadharNo',
      'aadhaarNo',
      'aadharNumber',
      'aadharCardNo',
    ]);
  }

  private setLeftEmpOptionsList(
    list: Record<string, unknown>[],
    target: 'replacement' | 'rehiring'
  ): Record<string, unknown>[] {
    if (target === 'rehiring') {
      this.prevLeftEmpOptionsList = list;
      this.prevLeftEmpOptionsSubject.next(list);
    } else {
      this.leftEmpOptionsList = list;
      this.leftEmpOptionsSubject.next(list);
    }
    return list;
  }

  private publishLeftEmpOptions(target: 'replacement' | 'rehiring'): void {
    if (target === 'rehiring') {
      this.prevLeftEmpOptionsSubject.next([...this.prevLeftEmpOptionsList]);
    } else {
      this.leftEmpOptionsSubject.next([...this.leftEmpOptionsList]);
    }
  }

  /** Typed text, payroll id, or selected row — used for LeftEmpSearch API calls. */
  private getLeftEmpSearchText(value: unknown): string {
    if (value == null) {
      return '';
    }
    if (typeof value === 'object' && !Array.isArray(value)) {
      return this.getLeftEmpLookupId(value as Record<string, unknown>);
    }
    const text = String(value).trim();
    if (text.includes('|')) {
      return text.split('|')[0]?.trim() || text;
    }
    return text;
  }

  private mergeLeftEmpOptions(
    existing: Record<string, unknown>[],
    incoming: Record<string, unknown>[]
  ): Record<string, unknown>[] {
    const merged = [...incoming];
    for (const item of existing) {
      const id = this.getLeftEmpOptionValue(item);
      if (!id) {
        continue;
      }
      if (
        !merged.some(
          (row) => this.getLeftEmpOptionValue(row).toLowerCase() === id.toLowerCase()
        )
      ) {
        merged.push(item);
      }
    }
    return merged;
  }

  private filterSearchValue(value: any, field: string): string {
    if (typeof value === 'string') {
      return value;
    }
    return value?.[field] || '';
  }

  getEmployeeCampusDetails(): void {
    this.spinner.show();
    this.hrHttpService.getEmpCampuses().subscribe({
      next: (res: any) => {
        this.campusOptionsList = res?.data ?? res ?? [];
        this.campus.setValue('');
        this.campus2.setValue('', { emitEvent: false });
        this.selectedCampusId = null;
        this.spinner.hide();
      },
      error: (err: any) => { this.spinner.hide(); console.error(err); },
    });
  }

  getMrfBasicInfo(): void {
    this.spinner.show();
    this.hrHttpService.getMrfBasicInfo().subscribe({
      next: (res: any) => {
        const data = res?.data ?? res;

        this.employeeTypeOptionsList = data?.employeeTypes ?? [];
        this.employeeType.setValue('');
        this.employeeType2.setValue('', { emitEvent: false });

        this.subjectOptionsList = this.normalizeSubjects(data?.subjects ?? []);
        this.subject.setValue('');
        this.subject2.setValue('', { emitEvent: false });
        this.spinner.hide();
      },
      error: (err) => { this.spinner.hide(); console.error(err); },
    });
  }

  getMrfTeachTypes(): void {
    this.spinner.show();
    this.hrHttpService.getMrfTeachTypes().subscribe({
      next: (res: any) => {
        this.teachingTypeOptionsList = this.hrHttpService.parseMrfTeachTypesResponse(res);
        this.teachingTypeList$.next(this.teachingTypeOptionsList);
        this.teachingType.setValue('');
        this.teachingType2.setValue('', { emitEvent: false });
        this.selectedTeachingTypeId = null;
        this.clearDepartmentAndDesignation();
        this.spinner.hide();
      },
      error: (err) => { this.spinner.hide(); console.error(err); },
    });
  }

  onTeachingTypeSelectionChange(event: MatSelectChange): void {
    const selected = String(event.value ?? '').trim();
    if (!selected) {
      this.selectedTeachingTypeId = null;
      this.clearDepartmentAndDesignation();
      return;
    }

    const campusSelected = String(this.campus2.value ?? '').trim();
    const employeeTypeSelected = String(this.employeeType2.value ?? '').trim();
    if (!campusSelected || !employeeTypeSelected) {
      const missing: string[] = [];
      if (!campusSelected) missing.push('Campus Name');
      if (!employeeTypeSelected) missing.push('Employee Type');
      this.hrAlert.show(
        `Please select ${missing.join(' and ')} before Teaching Type.`,
        'Warning'
      );
      this.teachingType.setValue('');
      this.teachingType2.setValue('', { emitEvent: false });
      this.selectedTeachingTypeId = null;
      this.clearDepartmentAndDesignation();
      return;
    }

    const teachType = this.teachingTypeOptionsList.find(
      (t) => this.hrHttpService.getMrfTeachTypeLabel(t) === selected
    );
    const typeId = teachType?.typeId;
    if (typeId == null) {
      this.selectedTeachingTypeId = null;
      this.clearDepartmentAndDesignation();
      return;
    }
    this.selectedTeachingTypeId = typeId;
    this.loadTypeWiseDepartmentsAndDesignations(typeId);
    this.refreshHeadCountInfo();
  }

  private syncSubjectVisibilityAfterRoleTypeLoad(data: Record<string, unknown>): void {
    const desigName =
      String(this.designation2.value ?? this.posDesignation2.value ?? '').trim() ||
      this.resolveDesignationNameFromData(data, this.resolveDepartment(data));
    this.updateSubjectVisibilityFromDesignation(desigName || null);
  }

  private loadTypeWiseDepartmentsAndDesignations(typeId: number | string): void {
    this.spinner.show();
    this.hrHttpService.getMrfTypeWiseDesigAndDepartments(typeId).subscribe({
      next: (res: any) => {
        const data = res?.data ?? res;
        const rawList = Array.isArray(data) ? data : [];
        this.departmentOptionsList = this.normalizeDepartments(rawList);
        this.departmentList$.next(this.departmentOptionsList);
        this.department.setValue('');
        this.department2.setValue('', { emitEvent: false });
        this.clearDesignationFields();
        this.spinner.hide();
      },
      error: (err) => { this.spinner.hide(); console.error(err); },
    });
  }

  private clearDepartmentAndDesignation(): void {
    this.departmentOptionsList = [];
    this.departmentList$.next([]);
    this.department.setValue('');
    this.department2.setValue('', { emitEvent: false });
    this.lastSelectedDepartment = '';
    this.lastSelectedDesignation = '';
    this.clearDesignationFields();
    this.clearSubjectFields();
  }

  designationChange(_item: any): void {
    // Department → designation/subject cascade is handled in department2.valueChanges.
  }

  /** Map API items to { deptName, designations } for department / designation dropdowns. */
  private normalizeDepartments(raw: unknown): any[] {
    if (!Array.isArray(raw)) {
      return [];
    }
    return raw.map((item) => {
      if (typeof item === 'string') {
        return { deptName: item, designations: [], desigDetails: [] };
      }
      const deptName =
        item?.deptName ??
        item?.departmentName ??
        item?.deptname ??
        item?.name ??
        '';
      const designations = item?.designations ?? item?.desigDetails ?? item?.designationList ?? [];
      return { ...item, deptName: String(deptName), designations, desigDetails: designations };
    });
  }

  private Filter(value: string, list: any[], name: string, altNames: string[] = []): any[] {
    const fields = [name, ...altNames];
    const hasLabel = (item: any): boolean =>
      fields.some((field) => {
        const label = item?.[field];
        return label != null && String(label).trim() !== '';
      });

    const matchesSearch = (item: any): boolean => {
      const searchValue = value.trim().toLocaleLowerCase();
      if (!searchValue) {
        return hasLabel(item);
      }
      return fields.some((field) => {
        const label = item?.[field];
        return (
          label != null &&
          String(label).trim().toLocaleLowerCase().includes(searchValue)
        );
      });
    };

    return list.filter(matchesSearch);
  }

  setDesignationListForDepartment(
    dept: any,
    preferredDesigName?: string,
    preferredDesigId?: unknown,
    preserveDesignation = false
  ): void {
    const previous = preserveDesignation
      ? String(
          preferredDesigName ?? this.designation2.value ?? this.posDesignation2.value ?? ''
        ).trim()
      : String(preferredDesigName ?? '').trim();
    let list = this.normalizeDesignationList(dept?.designations ?? dept?.desigDetails ?? []);

    if (
      preserveDesignation &&
      previous &&
      !list.some((d: any) => d.desigName.toLowerCase() === previous.toLowerCase())
    ) {
      list.push({ desigName: previous, desigId: preferredDesigId ?? null });
    }

    this.designationOptionsList$.next(list);

    const selectedName = preserveDesignation
      ? this.resolveDesignationName(list, previous)
      : previous
        ? this.resolveDesignationName(list, previous)
        : '';
    this.setSelectPair(this.designation2, this.designation, selectedName);
    this.setSelectPair(this.posDesignation2, this.posDesignation, selectedName);
    if (selectedName) {
      this.lastSelectedDesignation = selectedName;
    }
    this.updateSubjectVisibilityFromDesignation(selectedName || null);
  }

  onDesignationSelectionChange(event: MatSelectChange): void {
    this.updateSubjectVisibilityFromDesignation(event.value);
  }

  private updateSubjectVisibilityFromDesignation(desigName: string | null | undefined): void {
    const name = String(desigName ?? '').trim();
    if (!name) {
      this.hideSubjectSelection();
        return;
    }

    const desig = this.findDesignationByName(name);
    if (!desig) {
      this.hideSubjectSelection();
      return;
    }

    // Teach and Non-teach: show Subject only when designation.subjetdesignation is true
    this.showSubjectSelection = this.isSubjectDesignationRequired(desig);
    if (!this.showSubjectSelection) {
      this.clearSubjectFields();
    }
  }

  isNonTeachRoleType(value?: unknown): boolean {
    const role = String(value ?? this.teachingType2.value ?? '')
      .trim()
      .toUpperCase()
      .replace(/[_-]+/g, ' ');
    return role === 'NON TEACH' || role.includes('NON TEACH');
  }

  /** Skip headCountInfo only when teaching type is NON TEACH SUBJECT (not by designation). */
  isNonTeachSubjectWise(): boolean {
    if (!this.isNonTeachRoleType()) {
      return false;
    }

    const role = String(this.teachingType2.value ?? '')
      .trim()
      .toUpperCase()
      .replace(/[_-]+/g, ' ');
    return (
      role.includes('SUBJECT WISE') ||
      role.includes('SUBJECTWISE') ||
      role.includes('SUBJECT')
    );
  }

  private shouldFetchHeadCountInfo(): boolean {
    return this.selectedCampusId != null && !this.isNonTeachSubjectWise();
  }

  private resetHeadCountInfo(): void {
    this.headcountError = '';
    this.headcountBudgetTotal = 0;
    this.budgetedPositions = '';
    this.updateAvailableHeadcountDisplay();
  }

  private hideSubjectSelection(): void {
    this.showSubjectSelection = false;
    this.clearSubjectFields();
  }

  private normalizeDesignationList(list: any[]): any[] {
    return list
      .map((item) => {
        if (typeof item === 'string') {
          const desigName = item.trim();
          return { desigName, designation: desigName };
        }
        const desigName = String(
          item?.desigName ?? item?.designation ?? item?.designationName ?? ''
        ).trim();
        return { ...item, desigName };
      })
      .filter((item) => item.desigName);
  }

  private normalizeSubjects(raw: unknown): any[] {
    if (!Array.isArray(raw)) {
      return [];
    }
    return raw
      .map((item) => {
        if (typeof item === 'string') {
          const subName = item.trim();
          return { subName, subjectName: subName };
        }
        const subName = String(
          item?.subName ?? item?.subjectName ?? item?.subject ?? ''
        ).trim();
        return { ...item, subName, subjectName: subName || item?.subjectName };
      })
      .filter((item) => item.subName);
  }

  private isSubjectDesignationRequired(desig: any): boolean {
    const flag =
      desig?.subjetdesignation ??
      desig?.subjectDesignation ??
      desig?.subjectdesignation ??
      desig?.isSubjectDesignation ??
      desig?.subjectRequired;
    return flag === true || flag === 1 || flag === 'true' || flag === 'Y' || flag === 'y';
  }

  private findDesignationByName(desigName: string): any | undefined {
    const list = this.designationOptionsList$.getValue();
    const normalized = desigName.trim().toLowerCase();
    return list.find((d: any) => {
      const name = (d?.desigName ?? d?.designation ?? '').toString().trim().toLowerCase();
      return name === normalized;
    });
  }

  private clearSubjectFields(): void {
    this.subject.setValue('');
    this.subject2.setValue('', { emitEvent: false });
  }

  private clearDesignationFields(): void {
    this.designationOptionsList$.next([]);
    this.designation.setValue('');
    this.posDesignation.setValue('');
    this.designation2.setValue('', { emitEvent: false });
    this.posDesignation2.setValue('', { emitEvent: false });
    this.lastSelectedDesignation = '';
    this.showSubjectSelection = false;
  }

  private resolveDesignationName(list: any[], preferred: string): string {
    if (!preferred) {
      return '';
    }
    const match = list.find((d: any) => {
      const name = (d?.desigName ?? d?.designation ?? '').toString().trim();
      return name && name.toLowerCase() === preferred.toLowerCase();
    });
    return match ? (match?.desigName ?? match?.designation ?? '') : preferred;
  }

  onCampusSelectionChange(event: MatSelectChange): void {
    const campus = this.campusOptionsList.find((c) => c?.campusName === event.value);
    if (campus) {
      this.onCampusSelect(campus);
    }
  }

  private handleCampusChange(campusName: unknown): void {
    const normalized = String(campusName ?? '').trim();

    if (!normalized) {
      this.lastSelectedCampus = '';
      this.selectedCampusId = null;
      this.budgetedPositions = '';
      this.headcountBudgetTotal = 0;
      this.updateAvailableHeadcountDisplay();
      this.setReportingManagerOptionsList([]);
      this.reportingManager.setValue('');
      this.reportingManager2.setValue('', { emitEvent: false });
      return;
    }

    const campus = this.campusOptionsList.find(
      (c) => String(c?.campusName ?? '').trim() === normalized
    );
    this.selectedCampusId = campus?.campusId ?? null;

    if (this.lastSelectedCampus !== normalized) {
      this.clearFormFieldsOnCampusChange();
      this.lastSelectedCampus = normalized;
    }

    this.refreshHeadCountInfo();
    this.getMrfReportingManagers();
  }

  /** Resets all form fields except the selected campus. */
  private clearFormFieldsOnCampusChange(): void {
    this.employeeType.setValue('');
    this.employeeType2.setValue('', { emitEvent: false });
    this.teachingType.setValue('');
    this.teachingType2.setValue('', { emitEvent: false });
    this.selectedTeachingTypeId = null;
    this.clearDepartmentAndDesignation();
    this.workload.setValue('');

    this.posTab = 'Replacement';
    this.hiringReason.setValue('');
    this.reasonDescription.setValue('', { emitEvent: false });
    this.clearReplacementFields();

    this.rehiring = false;
    this.prevChaitanyaId.setValue('');
    this.rehiringDob.setValue('');
    this.aadharNo.setValue('');
    this.clearRehiringAadhaarUploads();
    this.prevRehiringEmployeeDetails = null;
    this.prevLeftEmpOptionsList = [];
    this.prevLeftEmpOptionsSubject.next([]);

    this.noOfHeadcount.setValue('1', { emitEvent: false });
    this.budgetedPositions = '';
    this.headcountBudgetTotal = 0;
    this.updateAvailableHeadcountDisplay();

    this.qualification.setValue('');
    this.qualification2.setValue('', { emitEvent: false });
    this.additionalQualification.setValue('');
    this.additionalQualification2.setValue('', { emitEvent: false });
    this.yearsOfExp.setValue('');
    this.skills.setValue('');

    this.reportingManager.setValue('');
    this.reportingManager2.setValue('', { emitEvent: false });

    this.posDesignation.setValue('');
    this.posDesignation2.setValue('', { emitEvent: false });
    this.costCenter.setValue('');
    this.costCenter2.setValue('', { emitEvent: false });

    this.showSubjectSelection = false;
  }

  onCampusSelect(item: any): void {
    this.selectedCampusId = item?.campusId ?? null;
    this.refreshHeadCountInfo();
    this.getMrfReportingManagers();
  }

  trackByCampusId(_index: number, item: any): string | number {
    return item?.campusId ?? item?.campusName ?? _index;
  }

  trackByLeftEmpId = (_index: number, item: Record<string, unknown>): string | number => {
    return this.getLeftEmpLookupId(item) || _index;
  };

  getMrfCostCenterDetails(): void {
    this.spinner.show();
    this.hrHttpService.getMrfCostCenterDetails().subscribe({
      next: (res: any) => {
        const data = res?.data ?? res;
        const rawList = Array.isArray(data)
          ? data
          : (data?.costCenters ?? data?.costCenterDetails ?? data?.costCenterList ?? []);
        this.costCenterOptionsList = this.normalizeCostCenters(rawList);
        this.costCenter.setValue('');
        this.costCenter2.setValue('', { emitEvent: false });
        this.spinner.hide();
      },
      error: (err) => { this.spinner.hide(); console.error(err); },
    });
  }

  getMrfQualifications(): void {
    this.spinner.show();
    this.hrHttpService.getMrfQualifications().subscribe({
      next: (res: any) => {
        const rawList = this.hrHttpService.parseMrfQualificationsResponse(res);
        this.qualificationOptionsList$.next(this.normalizeQualifications(rawList));
        this.qualification.setValue('');
        this.qualification2.setValue('', { emitEvent: false });
        this.additionalQualification.setValue('');
        this.additionalQualification2.setValue('', { emitEvent: false });
        this.spinner.hide();
      },
      error: (err) => { this.spinner.hide(); console.error(err); },
    });
  }

  getMrfReportingManagers(): void {
    if (this.selectedCampusId == null || String(this.selectedCampusId).trim() === '') {
      this.setReportingManagerOptionsList([]);
      this.reportingManager.setValue('');
      this.reportingManager2.setValue('', { emitEvent: false });
      return;
    }

    this.spinner.show();
    this.hrHttpService.getMrfReportingManagers(this.selectedCampusId).subscribe({
      next: (res: any) => {
        const rawList = this.hrHttpService.parseMrfReportingManagersResponse(res);
        this.setReportingManagerOptionsList(
          this.hrHttpService.normalizeMrfReportingManagerList(rawList)
        );
        this.reportingManager.setValue('');
        this.reportingManager2.setValue('', { emitEvent: false });
        this.spinner.hide();
      },
      error: (err) => { this.spinner.hide(); console.error(err); },
    });
  }

  private setReportingManagerOptionsList(list: any[]): void {
    this.reportingManagerOptionsList = list;
    this.reportingManagerList$.next(list);
  }

  private normalizeQualifications(raw: unknown): any[] {
    if (!Array.isArray(raw)) {
      return [];
    }
    return raw.map((item) => {
      if (typeof item === 'string') {
        return { qualificationName: item };
      }
      const qualificationName = String(
        item?.qualificationName ??
          item?.qualification ??
          item?.qualName ??
          item?.educationQualification ??
          item?.educationQualificationName ??
          item?.eduQualification ??
          item?.name ??
          ''
      ).trim();
      if (!qualificationName) {
        return null;
      }
      return { ...item, qualificationName };
    }).filter((item): item is Record<string, unknown> => item != null);
  }

  getReportingManagerOptionLabel(item: Record<string, unknown> | null | undefined): string {
    return this.hrHttpService.buildMrfReportingManagerOptionLabel(item);
  }

  private normalizeCostCenters(raw: unknown): any[] {
    if (!Array.isArray(raw)) {
      return [];
    }
    return raw.map((item) => {
      if (typeof item === 'string') {
        return { costCenterName: item };
      }
      const costCenterName = String(
        item?.costCenterName ??
          item?.costCenter ??
          item?.name ??
          item?.description ??
          ''
      );
      return { ...item, costCenterName };
    });
  }

  refreshHeadCountInfo(): void {
    if (!this.shouldFetchHeadCountInfo()) {
      this.resetHeadCountInfo();
      return;
    }

    const subId =
      this.isNonTeachRoleType() || !this.showSubjectSelection
        ? null
        : this.resolveSubjectId();
    this.headcountError = '';
    this.spinner.show();
    this.hrHttpService
      .getMrfHeadCountInfoForMrfData(
        { roleType: String(this.teachingType2.value ?? '') },
        {
      campusId: this.selectedCampusId,
      typeId: this.selectedTeachingTypeId,
      deptId: this.resolveDeptId(),
      desigId: this.resolveDesigId(),
          subId,
        }
      )
      .subscribe({
        next: (res) => {
          this.headcountError = '';
          const parsed = this.hrHttpService.parseMrfHeadCountResponse(
            res,
            Number(this.noOfHeadcount.value) || 1
          );
          this.headcountBudgetTotal = parsed.budgetedPositions;
          this.budgetedPositions = String(parsed.budgetedPositions || '');
          this.updateAvailableHeadcountDisplay(parsed.availableHeadcount);
        this.spinner.hide();
      },
        error: (err) => {
          this.spinner.hide();
          const body = err?.error;
          const msg = (body && typeof body === 'object' && body.message)
            ? body.message
            : 'Unable to fetch headcount information.';
          this.headcountError = msg;
          this.hrAlert.show(msg, 'Error');
          this.headcountBudgetTotal = 0;
          this.budgetedPositions = '';
          this.updateAvailableHeadcountDisplay();
          console.error(err);
        },
      });
  }

  private updateAvailableHeadcountDisplay(availableFromApi?: number): void {
    if (availableFromApi != null && !Number.isNaN(availableFromApi)) {
      this.availableHeadcount = String(availableFromApi);
      return;
    }
    const requested = Math.max(0, Number(this.noOfHeadcount.value) || 0);
    this.availableHeadcount = String(Math.max(0, this.headcountBudgetTotal - requested));
  }

  leftEmpChange(item: Record<string, unknown>): void {
    if (item) {
      const lookupId = this.getLeftEmpLookupId(item);
      if (lookupId) {
        this.upsertLeftEmpOption(lookupId, item, 'replacement');
      }
      this.applyLeftEmpDetails(item);
      this.enrichLeftEmpDetailsFromReplacementApi(item);
    }
  }

  onLeftEmpSelected(event: MatAutocompleteSelectedEvent): void {
    const item = event.option.value as Record<string, unknown>;
    this.leftEmpChange(item);
  }

  onPrevLeftEmpSelected(event: MatAutocompleteSelectedEvent): void {
    if (this.rehiringDataLocked) {
      return;
    }
    const item = event.option.value as Record<string, unknown>;
    this.prevLeftEmpChange(item);
  }

  prevLeftEmpChange(item: Record<string, unknown>): void {
    if (this.rehiringDataLocked) {
      return;
    }
    if (item) {
      const lookupId = this.getLeftEmpLookupId(item);
      if (lookupId) {
        this.upsertLeftEmpOption(lookupId, item, 'rehiring');
      }
      this.applyPrevRehiringEmpDetails(item);
    }
  }

  displayLeftEmpLabel = (item: Record<string, unknown> | string | null): string => {
    if (!item) {
      return '';
    }
    if (typeof item === 'string') {
      return this.resolveLeftEmpDisplayLabel(item, 'replacement');
    }
    return this.getLeftEmpOptionLabel(item);
  };

  displayPrevLeftEmpLabel = (item: Record<string, unknown> | string | null): string => {
    if (!item) {
      return '';
    }
    if (typeof item === 'string') {
      return this.resolveLeftEmpDisplayLabel(item, 'rehiring');
    }
    return this.getLeftEmpOptionLabel(item);
  };

  private resolveLeftEmpDisplayLabel(
    value: string,
    target: 'replacement' | 'rehiring'
  ): string {
    const id = value.split('|')[0]?.trim() || value.trim();
    if (!id) {
      return '';
    }
    const list =
      target === 'rehiring' ? this.prevLeftEmpOptionsList : this.leftEmpOptionsList;
    const saved =
      target === 'rehiring'
        ? this.prevRehiringEmployeeDetails
        : this.replacementEmployeeDetails;
    const match =
      list.find(
        (row) => this.getLeftEmpOptionValue(row).toLowerCase() === id.toLowerCase()
      ) ??
      (saved && this.getLeftEmpLookupId(saved).toLowerCase() === id.toLowerCase()
        ? saved
        : null);
    return match ? this.getLeftEmpOptionLabel(match as Record<string, unknown>) : value;
  }

  getLeftEmpOptionValue(item: Record<string, unknown>): string {
    return this.getLeftEmpLookupId(item);
  }

  getLeftEmpOptionLabel(item: Record<string, unknown>): string {
    const payroll = this.getLeftEmpLookupId(item);
    const name = this.getLeftEmpName(item);
    const mobile = this.getLeftEmpMobile(item);
    const aadhar = this.getLeftEmpAadhar(item);
    const parts = [payroll, name, mobile, aadhar].filter((part) => !!part);
    return parts.join(' | ') || payroll || name || 'Employee';
  }

  private pickLeftEmpField(item: Record<string, unknown>, ...keys: string[]): string {
    for (const key of keys) {
      const val = item[key];
      if (val != null && String(val).trim() !== '' && String(val) !== '0') {
        return String(val).trim();
      }
    }
    return '';
  }

  private getLeftEmpName(item: Record<string, unknown>): string {
    return this.pickLeftEmpField(
      item,
      'empName',
      'replacementEmpName',
      'userName',
      'employeeName',
      'name'
    );
  }

  private getLeftEmpMobile(item: Record<string, unknown>): string {
    return this.pickLeftEmpField(
      item,
      'mobileNo',
      'mobileNumber',
      'mobile',
      'phoneNo',
      'contactNo'
    );
  }

  private getLeftEmpAadhar(item: Record<string, unknown>): string {
    return normalizeMrfAadhaarDigits(
      item['aadharNo'] ??
        item['aadhaarNo'] ??
        item['aadharNumber'] ??
        item['aadharCardNo'] ??
        ''
    );
  }

  private getLeftEmpDob(item: Record<string, unknown>): string {
    return this.pickLeftEmpField(item, 'dob', 'dateOfBirth', 'employeeDob', 'birthDate');
  }

  private setRehiringDobFromData(data: Record<string, unknown>): void {
    const dob = formatMrfDateForInput(
      this.getLeftEmpDob(data) ||
        this.fieldValue(data, 'rehiringDob', 'dob', 'dateOfBirth', 'employeeDob', 'birthDate')
    );
    this.rehiringDob.setValue(dob, { emitEvent: false });
  }

  private getLeftEmpLookupId(data: Record<string, unknown>): string {
    return (
      this.pickLeftEmpField(data, 'payrollId', 'payRollId') ||
      this.pickLeftEmpField(data, 'employeeId', 'empId', 'uniqueId', 'id')
    );
  }

  private getLeftEmpDesignation(item: Record<string, unknown>): string {
    return this.pickLeftEmpField(
      item,
      'replacementDesigName',
      'replacementDesignation',
      'desigName',
      'designation',
      'designationName',
      'empDesignation',
      'employeeDesignation',
      'desig'
    );
  }

  private getLeftEmpLastWorkingDate(item: Record<string, unknown>): unknown {
    const value = this.pickLeftEmpField(
      item,
      'lastWorkingDate',
      'lastWorkingDay',
      'lastWorkDate',
      'lwd',
      'dateOfLeaving',
      'dol',
      'relievingDate'
    );
    return value || null;
  }

  private applyLeftEmpDetails(data: Record<string, unknown>): void {
    const lookupId = this.getLeftEmpLookupId(data);
    const mapped = this.hrHttpService.mapLeftEmpSearchToViewData(data, lookupId);
    this.replacementEmployeeDetails = mapped;

    if (lookupId) {
      this.upsertLeftEmpOption(lookupId, mapped, 'replacement');
    }
    this.employeeId.setValue(mapped, { emitEvent: false });
    this.replacementName.setValue(this.getLeftEmpName(mapped));
    this.replacementDesignation.setValue(this.getLeftEmpDesignation(mapped));
    if (this.isReasonExit()) {
      this.lastWorkingDate.setValue(
        formatMrfDateForInput(
          mapped['lastWorkingDate'] ?? this.getLeftEmpLastWorkingDate(mapped)
        )
      );
    }

    if (!this.editingMrfId) {
      this.syncDepartmentAndDesignationFromReplacement(
        mapped,
        String(
          mapped['replacementDesigName'] ??
            this.getLeftEmpDesignation(mapped) ??
            ''
        )
      );
    }
  }

  /** Fallback when LeftEmpSearch row is missing designation/LWD fields. */
  private findLeftEmpSearchMatch(
    list: Record<string, unknown>[],
    lookupId: string
  ): Record<string, unknown> | null {
    const id = String(lookupId ?? '').trim().toLowerCase();
    if (!id || !list.length) {
      return null;
    }

    const matchesId = (emp: Record<string, unknown>): boolean => {
      const candidates = [
        this.getLeftEmpLookupId(emp),
        emp['empId'],
        emp['employeeId'],
        emp['uniqueId'],
        emp['payrollId'],
        emp['payRollId'],
      ]
        .map((val) => String(val ?? '').trim().toLowerCase())
        .filter(Boolean);
      return candidates.includes(id);
    };

    return list.find(matchesId) ?? list[0] ?? null;
  }

  private applyReplacementFromSearchResult(
    lookupId: string,
    match: Record<string, unknown>
  ): void {
    const mapped = this.hrHttpService.mapLeftEmpSearchToViewData(
      match,
      this.getLeftEmpLookupId(match) || lookupId
    );
    this.upsertLeftEmpOption(lookupId, mapped, 'replacement');
    this.applyLeftEmpDetails(mapped);
  }

  private enrichLeftEmpDetailsFromReplacementApi(item: Record<string, unknown>): void {
    if (this.editingMrfId) {
      return;
    }
    const lookupId = this.getLeftEmpLookupId(item);
    if (!lookupId) {
      return;
    }

    const searchName = this.getLeftEmpName(item);
    const hasDesig = !!this.getLeftEmpDesignation(item);
    const hasLwd = !!this.getLeftEmpLastWorkingDate(item);
    if (hasDesig && hasLwd) {
      return;
    }

    this.spinner.show();
    this.hrHttpService
      .getMrfReplacementByLookupId(lookupId)
      .pipe(finalize(() => this.spinner.hide()))
      .subscribe({
        next: (res) => {
          if (!res) {
            return;
          }
          const enriched = this.hrHttpService.mapLeftEmpSearchToViewData(
            { ...item, ...(res as Record<string, unknown>) },
            lookupId
          );
          if (searchName) {
            enriched['empName'] = searchName;
            enriched['userName'] = searchName;
            enriched['employeeName'] = searchName;
            enriched['replacementEmpName'] = searchName;
          }
          this.applyLeftEmpDetails(enriched);
        },
        error: () => {},
      });
  }

  private buildLeftEmpOptionFromData(
    lookupId: string,
    data: Record<string, unknown>
  ): Record<string, unknown> {
    const id = String(lookupId ?? '').trim();
    return {
      payrollId: id,
      payRollId: id,
      employeeId: id,
      empName: this.fieldValue(
        data,
        'empName',
        'replacementEmpName',
        'replacementEmployeeName',
        'replacementName',
        'userName',
        'employeeName',
        'name'
      ),
      userName: this.fieldValue(
        data,
        'replacementEmpName',
        'replacementEmployeeName',
        'replacementName',
        'userName',
        'employeeName',
        'empName',
        'name'
      ),
      employeeName: this.fieldValue(
        data,
        'replacementEmpName',
        'replacementEmployeeName',
        'employeeName',
        'empName',
        'name'
      ),
      mobileNo: this.fieldValue(
        data,
        'mobileNo',
        'mobileNumber',
        'mobile',
        'phoneNo',
        'contactNo'
      ),
      aadharNo: resolveMrfRehiringAadharNo(data as Record<string, unknown>) ||
        this.fieldValue(
          data,
          'aadharNo',
          'aadhaarNo',
          'aadharNumber',
          'aadharCardNo'
        ),
      designation: this.fieldValue(
        data,
        'replacementDesigName',
        'replacementDesignation',
        'designation',
        'desigName'
      ),
      lastWorkingDate: data['lastWorkingDate'] ?? data['lastWorkingDay'] ?? data['lwd'],
      dob: this.fieldValue(data, 'rehiringDob', 'dob', 'dateOfBirth', 'employeeDob', 'birthDate'),
    };
  }

  /** Show saved replacement employee in the dropdown immediately during edit. */
  private seedLeftEmpOptionFromSaved(
    lookupId: string,
    data: Record<string, unknown>,
    extras?: { repName?: string; repDesig?: string; lwd?: string }
  ): void {
    const id = String(lookupId ?? '').trim();
    if (!id) return;

    const savedOption = this.buildLeftEmpOptionFromData(id, {
      ...data,
      replacementEmpName: extras?.repName ?? data['replacementEmpName'],
      replacementDesigName: extras?.repDesig ?? data['replacementDesigName'],
      lastWorkingDate: extras?.lwd ?? data['lastWorkingDate'],
    });
    this.upsertLeftEmpOption(id, savedOption, 'replacement');
    this.applyLeftEmpDetails(savedOption);
  }

  /** Show saved previous employee in rehiring autocomplete during edit. */
  private seedPrevEmpOptionFromSaved(
    lookupId: string,
    data: Record<string, unknown>
  ): void {
    const id = String(lookupId ?? '').trim();
    if (!id) return;

    const savedOption = this.buildLeftEmpOptionFromData(id, data);
    this.upsertLeftEmpOption(id, savedOption, 'rehiring');
    this.lockRehiringFields();
    this.applyPrevRehiringEmpDetails(savedOption, {
      preserveAadhar: resolveMrfRehiringAadharNo(data),
    });
  }

  private applyPrevRehiringEmpDetails(
    data: Record<string, unknown>,
    options?: { preserveAadhar?: string }
  ): void {
    const lookupId = this.getLeftEmpLookupId(data);
    const mapped = this.hrHttpService.mapLeftEmpSearchToViewData(data, lookupId);
    this.prevRehiringEmployeeDetails = mapped;

    if (lookupId) {
      this.upsertLeftEmpOption(lookupId, mapped, 'rehiring');
    }
    this.prevChaitanyaId.setValue(lookupId, { emitEvent: false });

    const aadhar = resolveMrfRehiringAadharPreferringSaved(
      options?.preserveAadhar ? { aadharNo: options.preserveAadhar } : {},
      this.getLeftEmpAadhar(mapped)
    );
    if (aadhar) {
      this.aadharNo.setValue(aadhar, { emitEvent: false });
    }
    this.setRehiringDobFromData(mapped);
    if (lookupId) {
      this.lockRehiringFields();
    }
  }

  private lockRehiringFields(): void {
    this.rehiringDataLocked = true;
  }

  private unlockRehiringFields(): void {
    this.rehiringDataLocked = false;
  }

  private upsertLeftEmpOption(
    lookupId: string,
    item: Record<string, unknown>,
    target: 'replacement' | 'rehiring' = 'replacement'
  ): void {
    const id = String(lookupId ?? '').trim() || this.getLeftEmpLookupId(item);
    if (!id) return;

    const normalized = {
      ...item,
      payrollId: id,
      payRollId: id,
      employeeId: item['employeeId'] ?? id,
    };
    const list =
      target === 'rehiring' ? this.prevLeftEmpOptionsList : this.leftEmpOptionsList;
    const idx = list.findIndex(
      (emp) => this.getLeftEmpOptionValue(emp).toLowerCase() === id.toLowerCase()
    );

    const updated =
      idx >= 0
        ? [
            ...list.slice(0, idx),
            { ...list[idx], ...normalized },
            ...list.slice(idx + 1),
          ]
        : [...list, normalized];

    if (target === 'rehiring') {
      this.prevLeftEmpOptionsList = updated;
      this.prevLeftEmpOptionsSubject.next(updated);
    } else {
      this.leftEmpOptionsList = updated;
      this.leftEmpOptionsSubject.next(updated);
    }
  }

  /** Match replacement employee dept/designation to Basic Information dropdowns. */
  private syncDepartmentAndDesignationFromReplacement(
    data: Record<string, unknown>,
    desigName: string
  ): void {
    const deptName = String(
      data?.['deptName'] ?? data?.['departmentName'] ?? data?.['department'] ?? ''
    ).trim();
    if (!deptName) {
      return;
    }

    const dept = this.departmentOptionsList.find((d) => {
      const name = (d?.deptName ?? d?.departmentName ?? '').toString().trim();
      return name.toLowerCase() === deptName.toLowerCase();
    });

    if (dept) {
      const deptLabel = String(dept?.deptName ?? dept?.departmentName ?? '').trim();
      this.setSelectPair(this.department2, this.department, deptLabel);
      this.lastSelectedDepartment = deptLabel;
      this.setDesignationListForDepartment(dept, desigName, undefined, true);
    }
  }

  private clearReplacementFields(): void {
    this.replacementEmployeeDetails = null;
    this.employeeId.setValue('');
    this.leftEmpOptionsList = [];
    this.leftEmpOptionsSubject.next([]);
    this.replacementName.setValue('');
    this.replacementDesignation.setValue('');
    this.lastWorkingDate.setValue('');
  }

  private getMrfFormId(): string | null {
    return this.route.snapshot.queryParamMap.get('mrfId');
  }

  /** Loads master dropdowns, then GET .../mrf/getRequests?mrfFormId={id} for edit data. */
  private loadFormWithExistingData(mrfId: string): void {
    this.spinner.show();
    forkJoin({
      campuses: this.safeApiCall(this.hrHttpService.getEmpCampuses()),
      basicInfo: this.safeApiCall(this.hrHttpService.getMrfBasicInfo()),
      teachTypes: this.safeApiCall(this.hrHttpService.getMrfTeachTypes()),
      costCenters: this.safeApiCall(this.hrHttpService.getMrfCostCenterDetails()),
      qualifications: this.safeApiCall(this.hrHttpService.getMrfQualifications()),
      roles: this.safeApiCall(this.hrHttpService.getUserRoles()),
    })
      .pipe(
        switchMap((masterResults) => {
          this.applyMasterDataFromResponses({ ...masterResults, reportingManagers: [] });
          return this.hrHttpService.getMrfRequests(mrfId).pipe(
            map((mrfRequestRes) => this.hrHttpService.normalizeMrfRequestsResponse(mrfRequestRes))
          );
        }),
        switchMap((mrfData) => {
          const campusId = mrfData?.['campusId'] as string | number | null | undefined;
          const typeId = this.resolveTeachingTypeId(mrfData);
          return forkJoin({
            mrfData: of(mrfData),
            reportingManagers: this.safeApiCall(
              this.hrHttpService.getMrfReportingManagers(campusId ?? null)
            ),
            deptRes:
              typeId == null
                ? of(null)
                : this.hrHttpService.getMrfTypeWiseDesigAndDepartments(typeId).pipe(
            catchError((err) => {
              console.error('Department lookup failed', err);
                      return of(null);
            })
                  ),
          });
        })
      )
      .subscribe({
        next: ({ mrfData, reportingManagers, deptRes }) => {
          const mgrRaw = this.hrHttpService.parseMrfReportingManagersResponse(reportingManagers);
          this.setReportingManagerOptionsList(
            this.hrHttpService.normalizeMrfReportingManagerList(mgrRaw)
          );
          if (deptRes != null) {
            const data = (deptRes as { data?: unknown })?.data ?? deptRes;
            const rawList = Array.isArray(data) ? data : [];
            this.departmentOptionsList = this.normalizeDepartments(rawList);
            this.departmentList$.next(this.departmentOptionsList);
          }
          this.pendingEditData = mrfData;
          this.applyMrfDataToForm(mrfData);
          this.loadReplacementIfNeeded(mrfData, () => {
            this.loadRehiringIfNeeded(mrfData, () => {
              const finishLoad = () => {
            if (this.pendingEditData) {
              this.reapplyEditDropdownFields(this.pendingEditData);
            }
                if (this.isPosTab('Replacement')) {
                  this.publishLeftEmpOptions('replacement');
                  this.refreshLeftEmpAutocomplete('replacement');
                }
                if (this.rehiring) {
                  this.publishLeftEmpOptions('rehiring');
                  this.refreshLeftEmpAutocomplete('rehiring');
            }
            this.spinner.hide();
              };

              if (this.rehiring && this.editingMrfId) {
                this.loadRehiringAadhaarDocuments(this.editingMrfId, finishLoad);
              } else {
                finishLoad();
              }
            });
          });
        },
        error: (err) => {
          this.spinner.hide();
          console.error('Failed to load existing MRF form', err);
        },
      });
  }

  private safeApiCall<T>(request$: Observable<T>): Observable<T | null> {
    return request$.pipe(
      catchError((err) => {
        if ((err as { status?: number })?.status === 204) {
          return of(null);
        }
        console.error(err);
        return of(null);
      })
    );
  }

  private resolveTeachingTypeId(data: Record<string, unknown>): string | number | null {
    const rawTypeId = data['typeId'];
    if (rawTypeId != null && (typeof rawTypeId === 'string' || typeof rawTypeId === 'number')) {
      return rawTypeId;
    }

    const roleType = this.hrHttpService.getMrfTeachTypeLabel(data);
    if (!roleType) return null;

    return this.hrHttpService.matchMrfTeachingTypeIdFromRoleType(
      roleType,
      this.teachingTypeOptionsList
    );
  }

  private setSelectPair(
    displayControl: FormControl,
    filterControl: FormControl,
    value: string
  ): void {
    if (!value) return;
    const canonical = this.resolveCanonicalSelectValue(displayControl, value);
    // Keep filter empty so mat-select can match display value to a rendered mat-option.
    filterControl.setValue('', { emitEvent: true });
    displayControl.setValue(canonical, { emitEvent: false });
  }

  /** Map API / saved text to the exact mat-option [value] from the current option list. */
  private resolveCanonicalSelectValue(displayControl: FormControl, value: string): string {
    const normalized = value.trim().toLowerCase();
    if (!normalized) {
      return '';
    }

    if (displayControl === this.designation2 || displayControl === this.posDesignation2) {
      const list = this.designationOptionsList$.getValue();
      const match = list.find((item) => {
        const label = String(item?.desigName ?? item?.designation ?? item?.designationName ?? '')
          .trim()
          .toLowerCase();
        return label === normalized;
      });
      return String(match?.desigName ?? match?.designation ?? match?.designationName ?? value).trim();
    }

    if (displayControl === this.subject2) {
      const match = this.subjectOptionsList.find((item) => {
        const label = String(item?.subName ?? item?.subjectName ?? item?.subject ?? '')
          .trim()
          .toLowerCase();
        return label === normalized;
      });
      return String(match?.subName ?? match?.subjectName ?? match?.subject ?? value).trim();
    }

    if (displayControl === this.reportingManager2) {
      const match = this.reportingManagerOptionsList.find((item) => {
        const label = String(
          item?.reportingManagerName ??
            item?.managerName ??
            item?.empName ??
            item?.employeeName ??
            item?.userName ??
            item?.name ??
            ''
        )
          .trim()
          .toLowerCase();
        return label === normalized;
      });
      return String(
        match?.reportingManagerName ??
          match?.managerName ??
          match?.empName ??
          match?.employeeName ??
          match?.userName ??
          match?.name ??
          value
      ).trim();
    }

    return value.trim();
  }

  private applyMasterDataFromResponses(results: {
    campuses: unknown;
    basicInfo: unknown;
    teachTypes: unknown;
    costCenters: unknown;
    qualifications: unknown;
    reportingManagers?: unknown;
    roles?: unknown;
  }): void {
    if (results.roles != null && typeof results.roles === 'object') {
      this.userRoleDetails = results.roles as Record<string, unknown>;
      this.mrfPermissions = this.hrHttpService.setMrfHrmsPermissionsFromResponse(results.roles);
    }

    const campuses = results.campuses as { data?: unknown } | null;
    this.campusOptionsList = (campuses?.data ?? campuses ?? []) as any[];

    const basic =
      results.basicInfo == null
        ? null
        : ((results.basicInfo as { data?: unknown })?.data ?? results.basicInfo);
    const basicData = basic as { employeeTypes?: unknown[]; subjects?: unknown[] };
    this.employeeTypeOptionsList = (basicData?.employeeTypes ?? []) as any[];
    this.subjectOptionsList = this.normalizeSubjects(basicData?.subjects ?? []);

    this.teachingTypeOptionsList = this.hrHttpService.parseMrfTeachTypesResponse(
      results.teachTypes
    );
    this.teachingTypeList$.next(this.teachingTypeOptionsList);

    const costData = (results.costCenters as { data?: unknown })?.data ?? results.costCenters;
    const costRaw = Array.isArray(costData)
      ? costData
      : ((costData as { costCenters?: unknown[] })?.costCenters ??
        (costData as { costCenterDetails?: unknown[] })?.costCenterDetails ??
        (costData as { costCenterList?: unknown[] })?.costCenterList ??
        []);
    this.costCenterOptionsList = this.normalizeCostCenters(costRaw);

    const qualRaw = this.hrHttpService.parseMrfQualificationsResponse(results.qualifications);
    this.qualificationOptionsList$.next(this.normalizeQualifications(qualRaw));

    const mgrRaw = this.hrHttpService.parseMrfReportingManagersResponse(
      results.reportingManagers ?? []
    );
    this.setReportingManagerOptionsList(
      this.hrHttpService.normalizeMrfReportingManagerList(mgrRaw)
    );
  }

  private applyMrfDataToForm(data: Record<string, unknown>): void {
    this.posTab = this.resolvePositionTab(data);

    const campusName = this.fieldValue(data, 'campusName');
    const campus = this.resolveCampus(data);
    if (campus) {
      const label = String(campus?.campusName ?? campusName).trim();
      this.setSelectPair(this.campus2, this.campus, label);
      this.lastSelectedCampus = label;
      this.selectedCampusId = campus?.campusId ?? null;
      this.refreshHeadCountInfo();
    } else if (campusName) {
      this.ensureCampusOption(campusName, data['campusId']);
      this.setSelectPair(this.campus2, this.campus, campusName);
      this.lastSelectedCampus = campusName;
      if (data['campusId'] != null) {
        this.selectedCampusId = data['campusId'] as number | string;
      }
    }

    const empType = this.resolveEmployeeType(data);
    if (empType) {
      this.ensureEmployeeTypeOption(empType, data['empTypeId']);
      this.setSelectPair(this.employeeType2, this.employeeType, empType);
    }

    const roleType = this.hrHttpService.getMrfTeachTypeLabel(data);
    const teachType = this.resolveTeachingType(data);
    if (teachType) {
      const teachLabel = this.hrHttpService.getMrfTeachTypeLabel(teachType);
      this.setSelectPair(this.teachingType2, this.teachingType, teachLabel);
      this.selectedTeachingTypeId = teachType.typeId ?? null;
    } else if (roleType) {
        this.ensureTeachingTypeOption(roleType);
        this.setSelectPair(this.teachingType2, this.teachingType, roleType);
      this.selectedTeachingTypeId = null;
    }

    this.applyDepartmentDesignationFromData(data);
    this.syncSubjectVisibilityAfterRoleTypeLoad(data);

    const workload = data['workLoad'] ?? data['workload'];
    if (workload != null && String(workload).trim() !== '') {
      this.workload.setValue(normalizeMrfWorkloadValue(workload));
    }

    const hiringReason = this.fieldValue(data, 'hiringReason');
    if (hiringReason) {
      this.hiringReason.setValue(hiringReason);
    }

    const reasonDesc = this.normalizeReasonDescription(
      this.fieldValue(
      data,
      'reasonDescription',
      'hiringReasonDesc',
        'reasonDesc',
        'hiringReasonDescription'
      )
    );
    if (reasonDesc && this.isPosTab('Replacement')) {
      this.reasonDescription.setValue(reasonDesc, { emitEvent: false });
    }

    const rehiringState = this.resolveRehiringStateFromData(data);
    this.rehiring = rehiringState.enabled;
    this.showRehiringSection = rehiringState.showSection;

    const prevId = rehiringState.payrollId;
    if (this.rehiring && prevId) {
      this.seedPrevEmpOptionFromSaved(prevId, data);
    }

    const aadhar = resolveMrfRehiringAadharNo(data);
    if (aadhar) {
      this.aadharNo.setValue(aadhar);
    }

    if (this.rehiring) {
      this.setRehiringDobFromData(data);
    }

    const qualName = this.resolveQualificationName(data);
    if (qualName) {
      this.ensureQualificationOption(qualName, data['qualificationId']);
      this.setSelectPair(this.qualification2, this.qualification, qualName);
    }

    const addQual = this.fieldValue(data, 'additionalQualification');
    if (addQual) {
      this.setSelectPair(this.additionalQualification2, this.additionalQualification, addQual);
    }

    const yearsExp = resolveMrfYearsOfExperience(data as Record<string, unknown>);
    if (yearsExp !== '') {
      this.yearsOfExp.setValue(normalizeMrfYearsOfExperienceValue(yearsExp));
    }

    const skillsVal = this.fieldValue(data, 'skills');
    if (skillsVal) {
      this.skills.setValue(skillsVal);
    }

    const headcountVal = data['noOfHeadcount'] ?? data['headcount'];
    if (headcountVal != null && String(headcountVal).trim() !== '') {
      this.noOfHeadcount.setValue(String(headcountVal), { emitEvent: false });
    }

    const managerName = this.resolveReportingManagerName(data);
    if (managerName) {
      this.ensureReportingManagerOption(managerName, data['reportingManagerId'], data);
      this.setSelectPair(this.reportingManager2, this.reportingManager, managerName);
    }

    this.applyReplacementFieldsFromData(data);
    this.refreshHeadCountInfo();
    this.refreshSelectOptionLists();
  }

  /** Re-bind dropdowns after async replacement lookup (must not overwrite getRequests data). */
  private reapplyEditDropdownFields(data: Record<string, unknown>): void {
    this.applyDepartmentDesignationFromData(data);

    const qualName = this.resolveQualificationName(data);
    if (qualName) {
      this.ensureQualificationOption(qualName, data['qualificationId']);
      this.setSelectPair(this.qualification2, this.qualification, qualName);
    }

    const managerName = this.resolveReportingManagerName(data);
    if (managerName) {
      this.ensureReportingManagerOption(managerName, data['reportingManagerId'], data);
      this.setSelectPair(this.reportingManager2, this.reportingManager, managerName);
    }

    const reasonDesc = this.normalizeReasonDescription(
      this.fieldValue(
        data,
        'reasonDescription',
        'hiringReasonDesc',
        'reasonDesc',
        'hiringReasonDescription'
      )
    );
    if (reasonDesc && this.isPosTab('Replacement')) {
      this.reasonDescription.setValue(reasonDesc, { emitEvent: false });
    }

    this.refreshSelectOptionLists();
  }

  /** Re-run mat-select filter pipes after option lists were updated during edit load. */
  private refreshSelectOptionLists(): void {
    const filters = [
      this.campus,
      this.employeeType,
      this.teachingType,
      this.department,
      this.designation,
      this.subject,
      this.qualification,
      this.additionalQualification,
      this.reportingManager,
      this.posDesignation,
    ];

    for (const filter of filters) {
      filter.setValue('', { emitEvent: true });
    }
  }

  private resolveCampus(data: Record<string, unknown>): any | undefined {
    const campusName = this.fieldValue(data, 'campusName');
    if (campusName) {
      return this.campusOptionsList.find(
        (c) =>
          String(c?.campusName ?? '')
            .trim()
            .toLowerCase() === campusName.toLowerCase()
      );
    }
    if (data['campusId'] != null) {
      return this.campusOptionsList.find(
        (c) => String(c?.campusId) === String(data['campusId'])
      );
    }
    return undefined;
  }

  private resolveEmployeeType(data: Record<string, unknown>): string {
    return this.fieldValue(data, 'empType');
  }

  private resolveTeachingType(data: Record<string, unknown>): any | undefined {
    const roleType = this.hrHttpService.getMrfTeachTypeLabel(data);
    if (!roleType) return undefined;

    // Exact match only — "NON TEACH" must not match "TEACH" via includes().
    const norm = this.normalizeLookupToken(roleType);
    return this.teachingTypeOptionsList.find((t) => {
      const label = this.normalizeLookupToken(
        this.hrHttpService.getMrfTeachTypeLabel(t)
      );
      return label === norm;
    });
  }

  private ensureTeachingTypeOption(roleType: string): void {
    const exists = this.teachingTypeOptionsList.some(
      (t) =>
        this.normalizeLookupToken(this.hrHttpService.getMrfTeachTypeLabel(t)) ===
        this.normalizeLookupToken(roleType)
    );
    if (!exists) {
      this.teachingTypeOptionsList = [
        ...this.teachingTypeOptionsList,
        { roleType, TeachType: roleType, typeId: null },
      ];
      this.teachingTypeList$.next(this.teachingTypeOptionsList);
    }
  }

  private normalizeLookupToken(value: string): string {
    return value.replace(/[\s_-]+/g, '').toLowerCase();
  }

  private ensureCampusOption(campusName: string, campusId: unknown): void {
    const exists = this.campusOptionsList.some(
      (c) =>
        String(c?.campusName ?? '')
          .trim()
          .toLowerCase() === campusName.toLowerCase()
    );
    if (!exists) {
      this.campusOptionsList = [
        ...this.campusOptionsList,
        { campusName, campusId: campusId ?? null },
      ];
    }
  }

  private ensureEmployeeTypeOption(empType: string, empTypeId: unknown): void {
    const exists = this.employeeTypeOptionsList.some(
      (item) =>
        String(item?.employeeType ?? item?.empType ?? '')
          .trim()
          .toLowerCase() === empType.toLowerCase()
    );
    if (!exists) {
      this.employeeTypeOptionsList = [
        ...this.employeeTypeOptionsList,
        { employeeType: empType, empType: empType, empTypeId: empTypeId ?? null },
      ];
    }
  }

  private ensureDepartmentOption(deptName: string, deptId: unknown): void {
    const exists = this.departmentOptionsList.some((d) => {
      const name = (d?.deptName ?? d?.departmentName ?? '').toString().trim();
      return name.toLowerCase() === deptName.toLowerCase();
    });
    if (!exists) {
      const entry = { deptName, departmentName: deptName, deptId, designations: [], desigDetails: [] };
      this.departmentOptionsList = [...this.departmentOptionsList, entry];
      this.departmentList$.next(this.departmentOptionsList);
    }
  }

  private resolveQualificationName(data: Record<string, unknown>): string {
    const byName = this.fieldValue(data, 'qualificationName');
    if (byName) return byName;

    const qualId = data['qualificationId'];
    if (qualId == null) return '';

    const match = this.qualificationOptionsList$.getValue().find(
      (q) => String(q?.qualificationId ?? q?.qualId ?? q?.id) === String(qualId)
    );
    return String(match?.qualificationName ?? match?.qualification ?? '').trim();
  }

  private resolveReportingManagerName(data: Record<string, unknown>): string {
    const managerId = data['reportingManagerId'];
    if (managerId != null) {
      const byId = this.reportingManagerOptionsList.find(
        (m) =>
          String(m?.managerId ?? m?.reportingManagerId ?? m?.empId ?? m?.uniqueId ?? m?.id) ===
          String(managerId)
      );
      const fromId = String(
        byId?.reportingManagerName ?? byId?.managerName ?? byId?.empName ?? ''
      ).trim();
      if (fromId) {
        return fromId;
      }
    }

    const byName = this.fieldValue(data, 'managerName', 'reportingManagerName');
    if (!byName) {
      return '';
    }

    const byNameMatch = this.reportingManagerOptionsList.find((m) => {
      const label = String(
        m?.reportingManagerName ?? m?.managerName ?? m?.empName ?? m?.employeeName ?? ''
      )
        .trim()
        .toLowerCase();
      return label === byName.toLowerCase();
    });
    return String(
      byNameMatch?.reportingManagerName ??
        byNameMatch?.managerName ??
        byNameMatch?.empName ??
        byName
    ).trim();
  }

  private applyDepartmentDesignationFromData(data: Record<string, unknown>): void {
    const dept = this.resolveDepartment(data);
    const desigName = this.resolveDesignationNameFromData(data, dept);
    const subName = this.resolveSubjectName(data);

    if (dept) {
      const deptLabel = String(dept?.deptName ?? dept?.departmentName ?? '').trim();
      this.setSelectPair(this.department2, this.department, deptLabel);
      this.lastSelectedDepartment = deptLabel;
      this.setDesignationListForDepartment(dept, desigName, data['desigId'], true);
    } else {
      const deptLabel = this.fieldValue(data, 'deptName');
      if (deptLabel) {
        this.ensureDepartmentOption(deptLabel, data['deptId'] ?? data['departmentId']);
        this.setSelectPair(this.department2, this.department, deptLabel);
      }
      if (desigName) {
        this.setSelectPair(this.designation2, this.designation, desigName);
        this.setSelectPair(this.posDesignation2, this.posDesignation, desigName);
      }
    }

    if (subName) {
      this.ensureSubjectOption(subName, data['subId'] ?? data['subjectId']);
      this.setSelectPair(this.subject2, this.subject, subName);
      this.lastSelectedDesignation =
        desigName ||
        String(this.designation2.value ?? this.posDesignation2.value ?? '').trim();
    }

    const currentDesig =
      desigName ||
      String(this.designation2.value ?? this.posDesignation2.value ?? '').trim();
    this.updateSubjectVisibilityFromDesignation(currentDesig || null);

    if (this.isPosTab('New Position')) {
      const newDesig =
        desigName ||
        this.fieldValue(
          data,
          'newPositionDesigName',
          'positionDesignation',
          'desigName',
          'designation'
        );
      if (newDesig) {
        this.setSelectPair(this.posDesignation2, this.posDesignation, newDesig);
      }
    } else if (desigName) {
      this.setSelectPair(this.designation2, this.designation, desigName);
    }
  }

  private resolveDepartment(data: Record<string, unknown>): any | undefined {
    const deptId = data['deptId'];
    if (deptId != null) {
      const byId = this.departmentOptionsList.find(
        (d) => String(d?.deptId ?? d?.departmentId ?? d?.id) === String(deptId)
      );
      if (byId) return byId;
    }

    const deptName = this.fieldValue(data, 'deptName');
    if (!deptName) return undefined;

    return this.departmentOptionsList.find((d) => {
      const name = (d?.deptName ?? d?.departmentName ?? '').toString().trim();
      return name.toLowerCase() === deptName.toLowerCase();
    });
  }

  private resolveDesignationNameFromData(data: Record<string, unknown>, dept?: any): string {
    const desigName = this.fieldValue(data, 'desigName');
    if (desigName) return desigName;

    const desigId = data['desigId'];
    if (desigId == null) return '';

    const list = dept
      ? (dept?.designations ?? dept?.desigDetails ?? this.designationOptionsList$.getValue())
      : this.designationOptionsList$.getValue();

    const match = (Array.isArray(list) ? list : []).find(
      (d: any) => String(d?.desigId ?? d?.designationId ?? d?.id) === String(desigId)
    );
    return String(match?.desigName ?? match?.designation ?? '').trim();
  }

  private ensureQualificationOption(qualificationName: string, qualificationId: unknown): void {
    const list = this.qualificationOptionsList$.getValue();
    const exists = list.some(
      (q) =>
        String(q?.qualificationName ?? q?.qualification ?? '')
          .trim()
          .toLowerCase() === qualificationName.toLowerCase()
    );
    if (!exists) {
      this.qualificationOptionsList$.next([
        ...list,
        { qualificationName, qualificationId: qualificationId ?? null },
      ]);
    }
  }

  private ensureReportingManagerOption(
    managerName: string,
    managerId: unknown,
    data?: Record<string, unknown>
  ): void {
    const exists = this.reportingManagerOptionsList.some(
      (m) =>
        String(m?.reportingManagerName ?? m?.managerName ?? '')
          .trim()
          .toLowerCase() === managerName.toLowerCase()
    );
    if (!exists) {
      const payrollId = String(
        data?.['reportingManagerPayrollId'] ?? data?.['managerPayrollId'] ?? ''
      ).trim();
      const desigName = String(
        data?.['reportingManagerDesigName'] ??
          data?.['managerDesigName'] ??
          data?.['managerDesignation'] ??
          ''
      ).trim();
      this.setReportingManagerOptionsList([
        ...this.reportingManagerOptionsList,
        {
          reportingManagerName: managerName,
          managerName,
          managerId: managerId ?? null,
          reportingManagerId: managerId ?? null,
          desigName,
          payrollId,
        },
      ]);
    }
  }

  private ensureSubjectOption(subName: string, subId: unknown): void {
    const exists = this.subjectOptionsList.some(
      (s) =>
        String(s?.subName ?? s?.subjectName ?? '')
          .trim()
          .toLowerCase() === subName.toLowerCase()
    );
    if (!exists) {
      this.subjectOptionsList = [
        ...this.subjectOptionsList,
        { subName, subjectName: subName, subId: subId ?? null },
      ];
    }
  }

  private resolveSubjectName(data: Record<string, unknown>): string {
    const subName = this.fieldValue(data, 'subjectName', 'subName', 'subject');
    if (subName) {
      const byNameMatch = this.subjectOptionsList.find((s) => {
        const label = String(s?.subName ?? s?.subjectName ?? s?.subject ?? '')
          .trim()
          .toLowerCase();
        return label === subName.toLowerCase();
      });
      return String(
        byNameMatch?.subName ?? byNameMatch?.subjectName ?? byNameMatch?.subject ?? subName
      ).trim();
    }

    const subId = data['subId'] ?? data['subjectId'];
    if (subId == null) return '';

    const match = this.subjectOptionsList.find(
      (s) => String(s?.subId ?? s?.subjectId ?? s?.id) === String(subId)
    );
    return String(match?.subName ?? match?.subjectName ?? match?.subject ?? '').trim();
  }

  private applyReplacementFieldsFromData(data: Record<string, unknown>): void {
    if (!this.isPosTab('Replacement') || !this.editingMrfId) {
      return;
    }

    const lookupId = this.hrHttpService.resolveMrfReplacementEmpId(data);
    if (!lookupId) {
      return;
    }

    this.upsertLeftEmpOption(
      lookupId,
      this.buildLeftEmpOptionFromData(lookupId, data),
      'replacement'
    );
  }

  private loadReplacementIfNeeded(
    data: Record<string, unknown>,
    onComplete?: () => void
  ): void {
    if (!this.isPosTab('Replacement')) {
      onComplete?.();
      return;
    }

    const lookupId = this.hrHttpService.resolveMrfReplacementEmpId(data);

    if (!lookupId) {
      onComplete?.();
      return;
    }

    const savedOption = this.buildLeftEmpOptionFromData(lookupId, data);

    this.searchLeftEmpByTarget(lookupId, 'replacement').subscribe({
        next: (res) => {
        const apiList = this.hrHttpService.normalizeLeftEmpSearchResults(res);
        const match = this.findLeftEmpSearchMatch(apiList, lookupId);
        if (match) {
          this.applyReplacementFromSearchResult(lookupId, match);
        } else {
          this.applyLeftEmpDetails(savedOption);
        }
          onComplete?.();
        },
        error: (err) => {
        console.error('currentAndLeftEmpSearch failed during edit load', err);
        this.upsertLeftEmpOption(lookupId, savedOption, 'replacement');
        this.applyLeftEmpDetails(savedOption);
          onComplete?.();
        },
      });
  }

  private loadRehiringIfNeeded(
    data: Record<string, unknown>,
    onComplete?: () => void
  ): void {
    if (!this.rehiring) {
      onComplete?.();
      return;
    }

    const lookupId = this.hrHttpService.resolveMrfRehiringPayrollId(data);
    const savedAadhar = resolveMrfRehiringAadharNo(data);
    if (!lookupId && !savedAadhar) {
      onComplete?.();
      return;
    }

    if (lookupId) {
      this.lockRehiringFields();
    }

    const lookupKey = lookupId || savedAadhar;
    const savedOption = this.buildLeftEmpOptionFromData(lookupKey, data);

    this.hrHttpService
      .searchLeftEmployees(lookupId, { aadharNo: savedAadhar || undefined })
      .subscribe({
      next: (res) => {
        const apiList = this.hrHttpService.normalizeLeftEmpSearchResults(res);
        const match = lookupId
          ? this.hrHttpService.findLeftEmpSearchMatch(apiList, lookupId)
          : this.hrHttpService.findLeftEmpSearchMatchByAadhar(apiList, savedAadhar);
        const merged = match ? { ...savedOption, ...match } : savedOption;
        const resolvedId =
          this.hrHttpService.getLeftEmpLookupId(merged) || lookupId || lookupKey;
        this.upsertLeftEmpOption(resolvedId, merged, 'rehiring');
        this.applyPrevRehiringEmpDetails(merged, { preserveAadhar: savedAadhar });
        onComplete?.();
      },
      error: (err) => {
        console.error('LeftEmpSearch failed during rehiring edit load', err);
        this.upsertLeftEmpOption(lookupKey, savedOption, 'rehiring');
        this.applyPrevRehiringEmpDetails(savedOption, { preserveAadhar: savedAadhar });
        onComplete?.();
      },
    });
  }

  private isRehiringFromData(data: Record<string, unknown>): boolean {
    const value = data['isReHiring'] ?? data['isRehiring'] ?? data['rehiring'];
    if (value === true || value === 1) return true;
    if (value === false || value === 0 || value == null) return false;
    const normalized = String(value).trim().toLowerCase();
    return normalized === 'true' || normalized === 'yes' || normalized === 'y' || normalized === '1';
  }

  private resolveRehiringStateFromData(data: Record<string, unknown>): {
    showSection: boolean;
    enabled: boolean;
    payrollId: string;
  } {
    const isRehiringFlag = this.isRehiringFromData(data);
    const payrollId = this.hrHttpService.resolveMrfRehiringPayrollId(data);

    return {
      showSection: isRehiringFlag || this.isPosTab('New Position'),
      enabled: isRehiringFlag,
      payrollId,
    };
  }

  private fieldValue(data: Record<string, unknown>, ...keys: string[]): string {
    for (const key of keys) {
      const val = data[key];
      if (val != null && String(val).trim() !== '' && String(val) !== '0') {
        return String(val).trim();
      }
    }
    return '';
  }

  private resolveReplacementEmpId(): string | null {
    const fromDetails = this.getLeftEmpLookupId(this.replacementEmployeeDetails ?? {});
    if (fromDetails) {
      return fromDetails;
    }
    const typed = String(this.employeeId.value ?? '').trim();
    if (!typed) {
      return null;
    }
    return typed.split('|')[0]?.trim() || typed;
  }

  private resolvePrevChaitanyaId(): string | null {
    const fromDetails = this.getLeftEmpLookupId(this.prevRehiringEmployeeDetails ?? {});
    if (fromDetails) {
      return fromDetails;
    }
    const typed = String(this.prevChaitanyaId.value ?? '').trim();
    if (!typed) {
      return null;
    }
    return typed.split('|')[0]?.trim() || typed;
  }

  private toPayloadNumber(value: unknown): number {
    if (value == null || value === '') return 0;
    const num = Number(value);
    return Number.isNaN(num) ? 0 : num;
  }

  private toNumericReplacementEmpId(...values: unknown[]): number {
    for (const value of values) {
      if (value == null || value === '') {
        continue;
      }
      const text = String(value).trim();
      if (!text || text === '0' || !/^\d+$/.test(text)) {
        continue;
      }
      const num = Number(text);
      if (Number.isFinite(num) && num !== 0) {
        return num;
      }
    }
    return 0;
  }

  private toPayloadIdOrNull(value: number): number | null {
    return value === 0 ? null : value;
  }

  private resolveReplacementEmpNumericId(): number {
    const details = (this.replacementEmployeeDetails ?? {}) as Record<string, unknown>;
    const fromDetails = this.toNumericReplacementEmpId(
      details['uniqueId'],
      details['UniqueId'],
      details['employeeUniqueId'],
      details['empUniqueId'],
      details['empId'],
      details['employeeId'],
      details['id'],
      details['replacementEmpId']
    );
    if (fromDetails) {
      return fromDetails;
    }
    const saved = this.pendingEditData ?? {};
    return this.toNumericReplacementEmpId(
      saved['replacementEmpId'],
      saved['uniqueId'],
      this.resolveReplacementEmpId()
    );
  }

  private resolvePrevChaitanyaNumericId(): number {
    const details = (this.prevRehiringEmployeeDetails ?? {}) as Record<string, unknown>;
    return this.toPayloadNumber(
      details['uniqueId'] ??
        details['empId'] ??
        details['employeeId'] ??
        details['prevChaitanyaId'] ??
        details['id']
    );
  }

  private resolveRehiringPayrollId(): string {
    if (!this.rehiring) return '';
    const details = (this.prevRehiringEmployeeDetails ?? {}) as Record<string, unknown>;
    return (
      this.pickLeftEmpField(details, 'payrollId', 'payRollId') ||
      this.resolvePrevChaitanyaId() ||
      ''
    );
  }

  private isFieldEmpty(value: unknown): boolean {
    return value == null || String(value).trim() === '';
  }

  private requireField(
    label: string,
    value: unknown,
    mode: 'enter' | 'select' = 'enter'
  ): string | null {
    if (this.isFieldEmpty(value)) {
      return `Please ${mode} ${label}.`;
    }
    return null;
  }

  validateMrfForm(): string | null {
    let error =
      this.requireField('Campus Name', this.campus2.value, 'select') ??
      this.requireField('Employee Type', this.employeeType2.value, 'select') ??
      this.requireField('Teaching Type', this.teachingType2.value, 'select') ??
      this.requireField('Department', this.department2.value, 'select') ??
      this.requireField('Designation', this.designation2.value, 'select');
    if (error) return error;

    if (this.showSubjectSelection) {
      error = this.requireField('Subject', this.subject2.value, 'select');
      if (error) return error;
    }

    if (!this.isNonTeachRoleType()) {
      error = getMrfWorkloadValidationError(this.workload.value);
      if (error) return error;
    }

    error = this.requireField('Reason for hiring', this.hiringReason.value);
    if (error) return error;

    if (this.isPosTab('Replacement')) {
      error =
        this.requireField('Reason Description', this.reasonDescription.value, 'select') ??
        this.requireField('Payroll id', this.employeeId.value, 'select');
      if (error) return error;

      if (this.isReasonExit()) {
        error = this.requireField('DOL', this.lastWorkingDate.value);
        if (error) return error;
      }
    } else {
      error = this.requireField('Designation', this.posDesignation2.value, 'select');
      if (error) return error;
    }

    if (this.rehiring) {
      error =
        this.requireField('Previous Payroll Id', this.prevChaitanyaId.value, 'select') ??
        this.requireField('Date of Birth', this.rehiringDob.value);
      if (error) return error;
      error = getMrfAadhaarValidationError(this.aadharNo.value, 'Aadhaar No.');
      if (error) return error;
    }

    error = this.requireField('No. of Headcount', this.noOfHeadcount.value);
    if (error) return error;

    const headcount = Number(this.noOfHeadcount.value);
    if (!Number.isFinite(headcount) || headcount < 1) {
      return 'No. of Headcount must be at least 1.';
    }

    error = this.requireField('Educational Qualification', this.qualification2.value, 'select');
    if (error) return error;

    error = getMrfYearsOfExperienceValidationError(this.yearsOfExp.value);
    if (error) return error;

    error = this.requireField('Skills', this.skills.value);
    if (error) return error;

    error = this.requireField('Reporting Manager', this.reportingManager2.value, 'select');
    if (error) return error;

    if (!this.resolveCampusId()) return 'Please select a valid Campus Name.';
    if (!this.resolveDeptId()) return 'Please select a valid Department.';
    if (!this.resolveDesigId()) return 'Please select a valid Designation.';
    if (this.showSubjectSelection && !this.resolveSubjectId()) {
      return 'Please select a valid Subject.';
    }
    if (!this.resolveQualificationId()) {
      return 'Please select a valid Educational Qualification.';
    }
    if (!this.resolveReportingManagerId()) {
      return 'Please select a valid Reporting Manager.';
    }
    if (this.isPosTab('Replacement') && !this.resolveReplacementEmpNumericId()) {
      return 'Please select a valid Payroll id for replacement.';
    }
    if (
      this.rehiring &&
      !this.resolvePrevChaitanyaNumericId() &&
      !this.resolvePrevChaitanyaId()
    ) {
      return 'Please select a valid Previous Payroll Id for rehiring.';
    }

    return null;
  }

  private buildSaveMrfFormPayload(): SaveMrfFormPayload {
    const subId = this.showSubjectSelection ? this.resolveSubjectId() : null;
    const dob = this.rehiring ? formatMrfDateForPayload(this.rehiringDob.value) : '';

    return {
      mrfFormId: this.editingMrfId ? Number(this.editingMrfId) : null,
      campusId: this.toPayloadNumber(this.resolveCampusId()),
      roleType: String(this.teachingType2.value ?? ''),
      empType: String(this.employeeType2.value ?? ''),
      deptId: this.toPayloadNumber(this.resolveDeptId()),
      desigId: this.toPayloadNumber(this.resolveDesigId()),
      subId: this.toPayloadNumber(subId),
      workLoad: this.toPayloadNumber(this.workload.value),
      positionType: this.posTab === 'Replacement' ? 'REPLACEMENT' : 'NEW_POSITION',
      hiringReason: String(this.hiringReason.value ?? ''),
      reasonDescription:
        this.posTab === 'Replacement' ? String(this.reasonDescription.value ?? '') : '',
      replacementEmpId: this.resolveReplacementEmpNumericId(),
      isReHiring: this.rehiring,
      prevChaitanyaId: this.toPayloadIdOrNull(this.resolvePrevChaitanyaNumericId()),
      aadharNo: normalizeMrfAadhaarDigits(this.aadharNo.value),
      qualificationId: this.toPayloadNumber(this.resolveQualificationId()),
      additionalQualification: String(this.additionalQualification2.value ?? ''),
      yearsOfExp: normalizeMrfYearsOfExperienceValue(this.yearsOfExp.value),
      skills: String(this.skills.value ?? ''),
      reportingManagerId: this.toPayloadNumber(this.resolveReportingManagerId()),
      dob,
      rehiringPayrollId: this.resolveRehiringPayrollId(),
    };
  }

  saveMrfForm(): void {
    if (this.isSaving || !this.canEditRequestForm) return;

    const validationError = this.validateMrfForm();
    if (validationError) {
      this.hrAlert.show(validationError, 'Warning');
      return;
    }

    const payload = this.buildSaveMrfFormPayload();

    this.isSaving = true;
    this.spinner.show();
    this.hrHttpService
      .saveMrfForm(payload)
      .pipe(
        switchMap((res) => {
          const mrfId =
            this.editingMrfId ?? this.extractMrfFormIdFromSaveResponse(res);
          const pending = this.rehiringAadhaarPendingFiles;
          if (mrfId && this.rehiring && pending.length) {
            return this.hrHttpService
              .uploadMrfDocuments(mrfId, pending, MRF_DOCUMENT_TYPE.AADHAR)
              .pipe(map(() => res));
          }
          return of(res);
        })
      )
      .subscribe({
      next: (res: any) => {
        this.isSaving = false;
        this.rehiringAadhaarPendingFiles = [];
        console.log('MRF saved successfully', res);
        this.hrAlert.showOnDashboard(
          this.editingMrfId
            ? 'Manpower Requisition Form updated successfully.'
            : 'Manpower Requisition Form submitted successfully.',
          'Success'
        );
        this.spinner.hide();
        this.router.navigate(['/hrDetails/mrfDashboard']);
      },
      error: (err) => {
        this.isSaving = false;
        console.error('MRF save failed', err);
        this.hrAlert.show('Failed to submit. Please try again.', 'Error');
        this.spinner.hide();
      },
    });
  }

  onCancel(): void {
    this.router.navigate(['/hrDetails/mrfDashboard']);
  }

  private resolveCampusId(): number | string | null {
    return this.selectedCampusId;
  }

  private resolveDeptId(): number | null {
    const selectedName = (this.department2.value ?? '').toString().trim();
    if (!selectedName) return null;
    const dept = this.departmentOptionsList.find(
      (d) => (d?.deptName ?? d?.departmentName ?? '').toString().trim().toLowerCase() === selectedName.toLowerCase()
    );
    return dept?.deptId ?? dept?.departmentId ?? dept?.id ?? null;
  }

  private resolveDesigId(): number | null {
    const selectedName = (
      this.isPosTab('New Position')
        ? this.posDesignation2.value ?? this.designation2.value
        : this.designation2.value
    )
      ?.toString()
      .trim();
    if (!selectedName) return null;
    const list = this.designationOptionsList$.getValue();
    const desig = list.find(
      (d) => (d?.desigName ?? d?.designation ?? '').toString().trim().toLowerCase() === selectedName.toLowerCase()
    );
    return desig?.desigId ?? desig?.designationId ?? desig?.id ?? null;
  }

  private resolveSubjectId(): number | null {
    const selectedName = (this.subject2.value ?? '').toString().trim();
    if (!selectedName) return null;
    const sub = this.subjectOptionsList.find((s) => {
      const label = (s?.subName ?? s?.subjectName ?? s?.subject ?? '')
        .toString()
        .trim()
        .toLowerCase();
      return label === selectedName.toLowerCase();
    });
    return sub?.subId ?? sub?.subjectId ?? sub?.id ?? null;
  }

  private resolveQualificationId(): number | null {
    const selectedName = (this.qualification2.value ?? '').toString().trim();
    if (!selectedName) return null;
    const qual = this.qualificationOptionsList$.getValue().find(
      (q) => (q?.qualificationName ?? '').toString().trim().toLowerCase() === selectedName.toLowerCase()
    );
    return qual?.qualificationId ?? qual?.qualId ?? qual?.id ?? null;
  }

  private resolveReportingManagerId(): number | null {
    const selectedName = (this.reportingManager2.value ?? '').toString().trim();
    if (!selectedName) return null;
    const mgr = this.reportingManagerOptionsList.find((m) => {
      const label = String(m?.reportingManagerName ?? m?.managerName ?? m?.empName ?? '')
        .trim()
        .toLowerCase();
      return label === selectedName.toLowerCase();
    });
    return mgr?.managerId ?? mgr?.reportingManagerId ?? mgr?.empId ?? mgr?.uniqueId ?? mgr?.id ?? null;
  }

  private resolvePositionTab(data: Record<string, unknown>): HmfPosTab {
    const raw = String(
      data['positionType'] ?? data['posType'] ?? data['position'] ?? ''
    ).toUpperCase();
    if (raw.includes('REPLACEMENT')) {
      return 'Replacement';
    }
    if (raw.includes('NEW')) {
      return 'New Position';
    }
    if (
      this.fieldValue(
        data,
        'replacementEmpId',
        'replacementPayrollId',
        'replacementEmployeeId'
      )
    ) {
      return 'Replacement';
    }
    return 'New Position';
  }

  /** Map backend reasonDescription to mat-select option values. */
  private normalizeReasonDescription(value: unknown): string {
    const text = String(value ?? '').trim();
    if (!text) {
      return '';
    }
    const lower = text.toLowerCase();
    const exact = this.reasonDescriptionOptions.find((opt) => opt.toLowerCase() === lower);
    if (exact) {
      return exact;
    }
    if (lower.includes('transfer')) {
      return 'Transferred';
    }
    if (lower.includes('exit') || lower.includes('resign') || lower.includes('left')) {
      return 'Exit';
    }
    if (lower.includes('promot')) {
      return 'Promoted';
    }
    return text;
  }

  setPosTab(tab: HmfPosTab): void {
    this.posTab = tab;
    if (tab === 'New Position') {
      this.reasonDescription.setValue('');
      const basicDesig = String(this.designation2.value ?? '').trim();
      const posDesig = String(this.posDesignation2.value ?? '').trim();
      if (basicDesig && !posDesig) {
        this.setSelectPair(this.posDesignation2, this.posDesignation, basicDesig);
      } else if (posDesig && !basicDesig) {
        this.setSelectPair(this.designation2, this.designation, posDesig);
      }
    }
  }

  onAadhaarInput(event: Event): void {
    syncMrfAadhaarControlFromInput(this.aadharNo, event);
  }

  onWorkloadInput(event: Event): void {
    syncMrfWorkloadControlFromInput(this.workload, event);
  }

  onYearsOfExpInput(event: Event): void {
    syncMrfYearsOfExperienceControlFromInput(this.yearsOfExp, event);
  }

  isPosTab(tab: HmfPosTab): boolean {
    return this.posTab === tab;
  }

  isReasonExit(): boolean {
    return String(this.reasonDescription.value ?? '').trim().toLowerCase() === 'exit';
  }

  get rehiringDobDisplay(): string {
    return formatMrfDateForDisplay(this.rehiringDob.value) || '';
  }

  get isRehiringFieldsLocked(): boolean {
    return this.rehiringDataLocked;
  }

  get prevChaitanyaIdDisplay(): string {
    const lookupId = this.resolvePrevChaitanyaId() ?? '';
    return lookupId ? this.resolveLeftEmpDisplayLabel(lookupId, 'rehiring') : '';
  }

  canAddMoreRehiringAadhaarFiles(): boolean {
    return this.rehiringAadhaarPendingFiles.length < this.maxRehiringAadhaarFiles;
  }

  onRehiringAadhaarAttachClick(event: Event, fileInput: HTMLInputElement): void {
    if (!this.canAddMoreRehiringAadhaarFiles()) {
      event.preventDefault();
      return;
    }
    fileInput.click();
  }

  onRehiringAadhaarFilesSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const files = Array.from(input.files ?? []);
    if (!files.length) {
      return;
    }

    const sizeMessage = getMrfUploadFileSizeValidationMessage(files);
    this.rehiringAadhaarUploadError = sizeMessage ?? '';
    if (sizeMessage) {
      this.hrAlert.show(sizeMessage, 'Warning');
      input.value = '';
      return;
    }

    if (!this.canAddMoreRehiringAadhaarFiles()) {
      this.hrAlert.show(
        `You can upload up to ${this.maxRehiringAadhaarFiles} Aadhaar file.`,
        'Warning'
      );
      input.value = '';
      return;
    }

    this.rehiringAadhaarUploadError = '';
    this.rehiringAadhaarPendingFiles = files.slice(0, this.maxRehiringAadhaarFiles);
    input.value = '';
  }

  removeRehiringAadhaarPendingFile(index: number): void {
    this.rehiringAadhaarPendingFiles = this.rehiringAadhaarPendingFiles.filter(
      (_, i) => i !== index
    );
    this.rehiringAadhaarUploadError = '';
  }

  getDocumentName(doc: MrfDocumentView): string {
    return this.hrHttpService.getMrfDocumentViewName(doc);
  }

  getDocumentIconLabel(doc: MrfDocumentView): string {
    return this.hrHttpService.getMrfDocumentIconLabel(doc);
  }

  canDownloadDocument(doc: MrfDocumentView): boolean {
    return this.hrHttpService.canDownloadMrfDocument(doc);
  }

  onDocumentDownload(doc: MrfDocumentView): void {
    this.spinner.show();
    this.hrHttpService
      .triggerMrfDocumentDownload(doc)
      .pipe(finalize(() => this.spinner.hide()))
      .subscribe({
        error: (err) => {
          console.error(err);
          this.hrAlert.show('Failed to download document.', 'Error');
        },
      });
  }

  isPdfFile(doc: MrfDocumentView): boolean {
    const name = this.getDocumentName(doc).toLowerCase();
    return name.endsWith('.pdf') || String((doc as { mimeType?: string })?.mimeType ?? '').includes('pdf');
  }

  isImageFile(doc: MrfDocumentView): boolean {
    return this.hrHttpService.isMrfImageDocument(doc);
  }

  isPendingPdfOrImage(file: File): boolean {
    return !this.isPendingImage(file);
  }

  isPendingImage(file: File): boolean {
    const name = file.name.toLowerCase();
    return (
      /\.(png|jpe?g|gif|webp|bmp)$/i.test(name) ||
      String(file.type ?? '').startsWith('image/')
    );
  }

  getRehiringAadhaarDocumentName(doc: MrfDocumentView): string {
    return this.getDocumentName(doc);
  }

  getRehiringAadhaarDocumentUrl(doc: MrfDocumentView): string {
    return this.hrHttpService.getMrfDocumentViewUrl(doc);
  }

  private loadRehiringAadhaarDocuments(mrfId: string, onComplete?: () => void): void {
    this.hrHttpService.getMrfDocumentsByMrfFormId(mrfId).subscribe({
      next: (docs) => {
        this.revokeRehiringAadhaarDocumentUrls();
        this.rehiringAadhaarDocuments = docs.filter((doc) =>
          this.hrHttpService.matchesMrfDocumentType(doc, MRF_DOCUMENT_TYPE.AADHAR)
        );
      },
      error: () => {},
      complete: () => onComplete?.(),
    });
  }

  private clearRehiringAadhaarUploads(): void {
    this.rehiringAadhaarPendingFiles = [];
    this.rehiringAadhaarUploadError = '';
    this.revokeRehiringAadhaarDocumentUrls();
    this.rehiringAadhaarDocuments = [];
  }

  private revokeRehiringAadhaarDocumentUrls(): void {
    for (const doc of this.rehiringAadhaarDocuments) {
      const url = doc?.blobUrl;
      if (url?.startsWith('blob:')) {
        URL.revokeObjectURL(url);
      }
    }
  }

  private extractMrfFormIdFromSaveResponse(res: unknown): string | null {
    if (res == null) {
      return null;
    }
    if (typeof res === 'number') {
      return String(res);
    }
    if (typeof res === 'string' && res.trim()) {
      return res.trim();
    }
    if (typeof res === 'object') {
      const root = res as Record<string, unknown>;
      const data = (root['data'] ?? root['result'] ?? root['response'] ?? root) as unknown;
      if (typeof data === 'number' || (typeof data === 'string' && data.trim())) {
        return String(data).trim();
      }
      if (data && typeof data === 'object' && !Array.isArray(data)) {
        const obj = data as Record<string, unknown>;
        const id = obj['mrfFormId'] ?? obj['mrfId'] ?? obj['id'];
        if (id != null && String(id).trim()) {
          return String(id).trim();
        }
      }
      const id = root['mrfFormId'] ?? root['mrfId'] ?? root['id'];
      if (id != null && String(id).trim()) {
        return String(id).trim();
      }
    }
    return null;
  }

  onRehiringChange(ev: Event): void {
    const el = ev.target as HTMLInputElement;
    this.rehiring = el.checked;
    if (!this.rehiring) {
      this.unlockRehiringFields();
      this.prevChaitanyaId.setValue('');
      this.rehiringDob.setValue('');
      this.aadharNo.setValue('');
      this.clearRehiringAadhaarUploads();
      this.prevRehiringEmployeeDetails = null;
      this.prevLeftEmpOptionsList = [];
    }
  }
}
